import { randomUUID } from 'node:crypto';
import { MessageList } from '@mastra/core/agent';
import type { MastraMessageContentV2 } from '@mastra/core/agent';
import { ErrorCategory, ErrorDomain, MastraError } from '@mastra/core/error';
import type { MastraMessageV1, MastraDBMessage, StorageThreadType } from '@mastra/core/memory';
import {
  MemoryStorage,
  normalizePerPage,
  calculatePagination,
  OBSERVATIONAL_MEMORY_TABLE_SCHEMA,
  TABLE_MESSAGES,
  TABLE_RESOURCES,
  TABLE_THREADS,
  TABLE_SCHEMAS,
  createStorageErrorId,
  storageMessageMatchesMetadataFilter,
  validateStorageMetadataFilter,
} from '@mastra/core/storage';

/**
 * Local constant for the observational memory table name.
 * Defined locally to avoid a static import that crashes on older @mastra/core
 * versions that don't export TABLE_OBSERVATIONAL_MEMORY.
 */
const OM_TABLE = 'mastra_observational_memory' as const;
const POSTGRES_MAX_BIND_PARAMETERS = 65535;
// Keep in sync with the message INSERT column list in saveMessages.
const MESSAGE_INSERT_BIND_PARAMETERS = 8;
const MAX_MESSAGES_PER_INSERT = Math.floor(POSTGRES_MAX_BIND_PARAMETERS / MESSAGE_INSERT_BIND_PARAMETERS);

/**
 * Columns added to the OM table after its initial release.
 * Used in `alterTable({ ifNotExists })` so that databases created on older
 * versions get the new columns automatically.
 *
 * When you add a column to OBSERVATIONAL_MEMORY_SCHEMA in @mastra/core,
 * you MUST also add it here — the unit test `om-migration-columns.test.ts`
 * will fail otherwise.
 */
export const OM_MIGRATION_COLUMNS: string[] = [
  'observedMessageIds',
  'observedTimezone',
  'bufferedObservations',
  'bufferedObservationTokens',
  'bufferedMessageIds',
  'bufferedReflection',
  'bufferedReflectionTokens',
  'bufferedReflectionInputTokens',
  'reflectedObservationLineCount',
  'bufferedObservationChunks',
  'isBufferingObservation',
  'isBufferingReflection',
  'lastBufferedAtTokens',
  'lastBufferedAtTime',
  'metadata',
];

/**
 * The OM schema is imported statically above: the peer dependency range
 * (`@mastra/core >= 1.49.0`) guarantees the export exists. This used to be a
 * dynamic `require` guarded by `typeof require === 'function'` for older core
 * versions, but esbuild rewrites the bare `require` identifier in the ESM
 * bundle to a shim that always throws, and the silent catch meant the
 * published ESM build skipped creating the OM table entirely (#18954).
 */
const _omTableSchema: Record<string, Record<string, any>> = OBSERVATIONAL_MEMORY_TABLE_SCHEMA;
import type {
  StorageResourceType,
  StorageListMessagesInput,
  StorageListMessagesByResourceIdInput,
  StorageListMessagesOutput,
  StorageListThreadsInput,
  StorageListThreadsOutput,
  CreateIndexOptions,
  StorageCloneThreadInput,
  StorageCopyThreadOutput,
  ThreadCloneMetadata,
  ObservationalMemoryRecord,
  ObservationalMemoryHistoryOptions,
  BufferedObservationChunk,
  CreateObservationalMemoryInput,
  UpdateActiveObservationsInput,
  UpdateBufferedObservationsInput,
  SwapBufferedToActiveInput,
  SwapBufferedToActiveResult,
  UpdateBufferedReflectionInput,
  SwapBufferedReflectionToActiveInput,
  CreateReflectionGenerationInput,
  UpdateObservationalMemoryConfigInput,
  PruneOptions,
  PruneResult,
  RetentionTablesDescriptor,
  TableRetentionPolicy,
  TABLE_NAMES,
} from '@mastra/core/storage';
import { parseSqlIdentifier } from '@mastra/core/utils';
import {
  PgDB,
  resolvePgConfig,
  generateTableSQL,
  generateIndexSQL,
  getSchemaName as dbGetSchemaName,
  getTableName as dbGetTableName,
} from '../../db';
import type { DbClient, PgDomainConfig } from '../../db';
import { runPrune, runBatchedDelete, resolveTargets } from '../../retention';

// Database row type that includes timezone-aware columns
type MessageRowFromDB = {
  id: string;
  content: string | any;
  role: string;
  type?: string;
  createdAt: Date | string;
  createdAtZ?: Date | string;
  threadId: string;
  resourceId: string;
};

function getSchemaName(schema?: string) {
  return schema ? `"${schema}"` : '"public"';
}

function getTableName({ indexName, schemaName }: { indexName: string; schemaName?: string }) {
  const quotedIndexName = `"${indexName}"`;
  return schemaName ? `${schemaName}.${quotedIndexName}` : quotedIndexName;
}

/**
 * Generate SQL placeholder string for IN clauses.
 * @param count - Number of placeholders to generate
 * @param startIndex - Starting index for placeholders (default: 1)
 * @returns Comma-separated placeholder string, e.g. "$1, $2, $3"
 */
function inPlaceholders(count: number, startIndex = 1): string {
  return Array.from({ length: count }, (_, i) => `$${i + startIndex}`).join(', ');
}

/**
 * Bind dates as UTC strings because node-postgres serializes Date parameters
 * for TIMESTAMP columns using the process's local timezone.
 */
function toUtcISOString(date: Date): string {
  return date.toISOString();
}

function dedupeMessagesForSave(messages: MastraDBMessage[]): MastraDBMessage[] {
  const deduped = new Map<string, MastraDBMessage>();
  for (const message of messages) {
    const existing = deduped.get(message.id);
    if (existing) {
      deduped.set(message.id, {
        ...message,
        createdAt: existing.createdAt,
      });
    } else {
      deduped.set(message.id, {
        ...message,
        createdAt: message.createdAt || new Date(),
      });
    }
  }
  return Array.from(deduped.values());
}

export class MemoryPG extends MemoryStorage {
  override readonly supportsPartialThreadUpdate = true;
  readonly supportsObservationalMemory = true;

  /**
   * Retention-eligible tables. `threads`, `messages`, and `resources` all anchor
   * on the timezone-aware `createdAtZ` mirror column (kept in sync by triggers),
   * and are indexed for fast batched deletes. Cascade order is enforced in
   * `prune()` (children before threads), not here. Observational memory has no
   * timestamp anchor and is deliberately excluded.
   */
  static override readonly retentionTables: RetentionTablesDescriptor = {
    messages: { table: TABLE_MESSAGES, column: 'createdAtZ', indexed: true },
    resources: { table: TABLE_RESOURCES, column: 'createdAtZ', indexed: true },
    threads: { table: TABLE_THREADS, column: 'createdAtZ', indexed: true },
  };

  #db: PgDB;
  #schema: string;
  #skipDefaultIndexes?: boolean;
  #indexes?: CreateIndexOptions[];

  /** Tables managed by this domain */
  static readonly MANAGED_TABLES = [TABLE_THREADS, TABLE_MESSAGES, TABLE_RESOURCES, OM_TABLE] as const;

  constructor(config: PgDomainConfig) {
    super();
    const { client, readClient, schemaName, skipDefaultIndexes, indexes } = resolvePgConfig(config);
    this.#db = new PgDB({ client, readClient, schemaName, skipDefaultIndexes });
    this.#schema = schemaName || 'public';
    this.#skipDefaultIndexes = skipDefaultIndexes;
    // Filter indexes to only those for tables managed by this domain
    this.#indexes = indexes?.filter(idx => (MemoryPG.MANAGED_TABLES as readonly string[]).includes(idx.table));
  }

  async init(): Promise<void> {
    await this.#db.createTable({ tableName: TABLE_THREADS, schema: TABLE_SCHEMAS[TABLE_THREADS] });
    await this.#db.createTable({ tableName: TABLE_MESSAGES, schema: TABLE_SCHEMAS[TABLE_MESSAGES] });
    await this.#db.createTable({ tableName: TABLE_RESOURCES, schema: TABLE_SCHEMAS[TABLE_RESOURCES] });

    // Reuse the module-level `_omTableSchema` (static import). Don't switch
    // this to `await import('@mastra/core/storage')`: that used to deadlock
    // `mastra build` output, because bundlers rewrite the dynamic import to
    // point at the entry chunk that statically depends on this file, so the
    // cycle never resolves when storage initializes during module
    // evaluation (#18298).
    const omSchema = _omTableSchema?.[OM_TABLE];

    if (omSchema) {
      await this.#db.createTable({
        tableName: OM_TABLE as any,
        schema: omSchema,
      });
      // Add new OM columns for backwards compatibility with existing databases
      await this.#db.alterTable({
        tableName: OM_TABLE as any,
        schema: omSchema,
        ifNotExists: OM_MIGRATION_COLUMNS,
      });
    }
    await this.#db.alterTable({
      tableName: TABLE_MESSAGES,
      schema: TABLE_SCHEMAS[TABLE_MESSAGES],
      ifNotExists: ['resourceId'],
    });
    if (omSchema) {
      // Create index on lookupKey for efficient OM queries
      const omTableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      await this.#db.createIndexFromStatement(
        'idx_om_lookup_key',
        `CREATE INDEX IF NOT EXISTS idx_om_lookup_key ON ${omTableName} ("lookupKey")`,
      );
    }
    await this.createDefaultIndexes();
    await this.createCustomIndexes();
  }

  /**
   * Lazily ensures a btree index exists on each configured policy's retention
   * anchor column so age-based `prune()` deletes stay fast on large tables.
   * Called from the prune path (not init) so only deployments that configure
   * retention pay the index's write/disk overhead. Best-effort: failures are
   * logged and pruning proceeds (correct, just slower).
   * Created even with `skipDefaultIndexes` — retention is an explicit opt-in,
   * so its supporting index is not part of the default index set.
   */
  private async ensureRetentionIndexes(policies: Record<string, TableRetentionPolicy>): Promise<void> {
    const prefix = this.#schema && this.#schema !== 'public' ? `${this.#schema}_` : '';
    for (const [key, entry] of Object.entries(MemoryPG.retentionTables)) {
      if (!entry.indexed || !policies[key]) continue;
      try {
        await this.#db.ensureIndex({
          indexName: `${prefix}mastra_${key}_retention_idx`,
          tableName: entry.table as TABLE_NAMES,
          column: entry.column,
        });
      } catch (error) {
        this.logger?.warn?.(`Failed to create retention index for ${entry.table}:`, error);
      }
    }
  }

  /**
   * Returns default index definitions for the memory domain tables.
   * @param schemaPrefix - Prefix for index names (e.g. "my_schema_" or "")
   */
  static getDefaultIndexDefs(schemaPrefix: string): CreateIndexOptions[] {
    return [
      {
        name: `${schemaPrefix}mastra_threads_resourceid_createdat_idx`,
        table: TABLE_THREADS,
        columns: ['resourceId', 'createdAt DESC'],
      },
      {
        name: `${schemaPrefix}mastra_messages_thread_id_createdat_idx`,
        table: TABLE_MESSAGES,
        columns: ['thread_id', 'createdAt DESC'],
      },
    ];
  }

  /**
   * Returns all DDL statements for this domain: tables (threads, messages, resources, OM), indexes.
   * Used by exportSchemas to produce a complete, reproducible schema export.
   */
  static getExportDDL(schemaName?: string): string[] {
    const statements: string[] = [];
    const parsedSchema = schemaName ? parseSqlIdentifier(schemaName, 'schema name') : '';
    const schemaPrefix = parsedSchema && parsedSchema !== 'public' ? `${parsedSchema}_` : '';
    const quotedSchemaName = dbGetSchemaName(schemaName);

    // Tables: threads, messages, resources
    for (const tableName of [TABLE_THREADS, TABLE_MESSAGES, TABLE_RESOURCES] as const) {
      statements.push(
        generateTableSQL({
          tableName,
          schema: TABLE_SCHEMAS[tableName],
          schemaName,
          includeAllConstraints: true,
        }),
      );
    }

    // Observational memory table (if schema available in this version of core)
    const omSchema = _omTableSchema?.[OM_TABLE];
    if (omSchema) {
      statements.push(
        generateTableSQL({
          tableName: OM_TABLE as any,
          schema: omSchema,
          schemaName,
          includeAllConstraints: true,
        }),
      );
      // idx_om_lookup_key index
      const fullOmTableName = dbGetTableName({ indexName: OM_TABLE, schemaName: quotedSchemaName });
      const idxPrefix = schemaPrefix ? `${schemaPrefix}` : '';
      statements.push(
        `CREATE INDEX IF NOT EXISTS "${idxPrefix}idx_om_lookup_key" ON ${fullOmTableName} ("lookupKey");`,
      );
    }

    // Default indexes
    for (const idx of MemoryPG.getDefaultIndexDefs(schemaPrefix)) {
      statements.push(generateIndexSQL(idx, schemaName));
    }

    return statements;
  }

  /**
   * Returns default index definitions for this instance's schema.
   */
  getDefaultIndexDefinitions(): CreateIndexOptions[] {
    const schemaPrefix = this.#schema !== 'public' ? `${this.#schema}_` : '';
    return MemoryPG.getDefaultIndexDefs(schemaPrefix);
  }

  /**
   * Creates default indexes for optimal query performance.
   */
  async createDefaultIndexes(): Promise<void> {
    if (this.#skipDefaultIndexes) {
      return;
    }

    for (const indexDef of this.getDefaultIndexDefinitions()) {
      try {
        await this.#db.createIndex(indexDef);
      } catch (error) {
        // Log but continue - indexes are performance optimizations
        this.logger?.warn?.(`Failed to create index ${indexDef.name}:`, error);
      }
    }
  }

  /**
   * Creates custom user-defined indexes for this domain's tables.
   */
  async createCustomIndexes(): Promise<void> {
    if (!this.#indexes || this.#indexes.length === 0) {
      return;
    }

    for (const indexDef of this.#indexes) {
      try {
        await this.#db.createIndex(indexDef);
      } catch (error) {
        // Log but continue - indexes are performance optimizations
        this.logger?.warn?.(`Failed to create custom index ${indexDef.name}:`, error);
      }
    }
  }

  async dangerouslyClearAll(): Promise<void> {
    await this.#db.clearTable({ tableName: TABLE_MESSAGES });
    await this.#db.clearTable({ tableName: TABLE_THREADS });
    await this.#db.clearTable({ tableName: TABLE_RESOURCES });
  }

  /**
   * Deletes rows older than the configured `maxAge` per table, in bounded,
   * batched, cancellable chunks. Tables are pruned children-first (messages and
   * resources before threads) since PostgreSQL has no FK cascade in this schema.
   * Unset tables are kept forever.
   *
   * When a `messages` policy is set, semantic-recall embeddings for pruned
   * messages are also swept from same-schema `memory_messages*` vector tables
   * (best-effort, mirroring `deleteThread`). Embeddings held in an external
   * vector store are out of reach and must be pruned by the operator.
   */
  async prune(policies: Record<string, TableRetentionPolicy>, options?: PruneOptions): Promise<PruneResult[]> {
    await this.ensureRetentionIndexes(policies);
    const targets = resolveTargets({
      policies,
      descriptor: MemoryPG.retentionTables,
      order: ['messages', 'resources', 'threads'],
    });
    const results = await runPrune({ db: this.#db, domain: 'memory', targets, options });
    if (policies['messages']) {
      await this.pruneOrphanedVectorRows(policies['messages'], options);
    }
    return results;
  }

  /**
   * Best-effort sweep of semantic-recall vector rows whose source message no
   * longer exists (e.g. it was just pruned), so recall doesn't keep returning
   * embeddings that resolve to nothing. Only same-schema default vector tables
   * (`memory_messages*`) are covered — the same set `deleteThread` cleans up.
   * Failures are logged, never thrown: vector cleanup must not fail the prune.
   */
  private async pruneOrphanedVectorRows(policy: TableRetentionPolicy, options?: PruneOptions): Promise<void> {
    try {
      const schemaName = this.#schema || 'public';
      const vectorTables = await this.#db.client.manyOrNone<{ tablename: string }>(
        `
        SELECT tablename
        FROM pg_tables
        WHERE schemaname = $1
        AND (tablename = 'memory_messages' OR tablename LIKE 'memory_messages_%')
      `,
        [schemaName],
      );

      const messagesTable = getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) });
      for (const { tablename } of vectorTables) {
        const vectorTableName = getTableName({ indexName: tablename, schemaName: getSchemaName(this.#schema) });
        await runBatchedDelete({
          deleteBatch: async limit => {
            const result = await this.#db.client.query(
              `
              DELETE FROM ${vectorTableName}
              WHERE ctid IN (
                SELECT v.ctid FROM ${vectorTableName} v
                WHERE v.metadata->>'message_id' IS NOT NULL
                AND NOT EXISTS (SELECT 1 FROM ${messagesTable} m WHERE m.id = v.metadata->>'message_id')
                LIMIT $1
              )
            `,
              [limit],
            );
            return result.rowCount ?? 0;
          },
          batchSize: policy.batchSize ?? 1000,
          options,
        });
      }
    } catch (error) {
      this.logger?.warn?.('Failed to sweep orphaned semantic-recall vector rows after prune:', error);
    }
  }

  /**
   * Normalizes message row from database by applying createdAtZ fallback
   */
  private normalizeMessageRow(row: MessageRowFromDB): Omit<MessageRowFromDB, 'createdAtZ'> {
    return {
      id: row.id,
      content: row.content,
      role: row.role,
      type: row.type,
      createdAt: row.createdAtZ || row.createdAt,
      threadId: row.threadId,
      resourceId: row.resourceId,
    };
  }

  async getThreadById({
    threadId,
    resourceId,
  }: {
    threadId: string;
    resourceId?: string;
  }): Promise<StorageThreadType | null> {
    return this.#getThreadById(this.#db.readClient, { threadId, resourceId });
  }

  /**
   * Thread lookup against an explicit client. Mutation paths pass the writer so
   * a lagging read replica cannot produce false not-found or stale metadata.
   */
  async #getThreadById(
    client: DbClient,
    { threadId, resourceId }: { threadId: string; resourceId?: string },
  ): Promise<StorageThreadType | null> {
    try {
      const tableName = getTableName({ indexName: TABLE_THREADS, schemaName: getSchemaName(this.#schema) });

      let query = `SELECT * FROM ${tableName} WHERE id = $1`;
      let params: any[] = [threadId];

      if (resourceId !== undefined) {
        query += ` AND "resourceId" = $2`;
        params.push(resourceId);
      }

      const thread = await client.oneOrNone<StorageThreadType & { createdAtZ: Date; updatedAtZ: Date }>(query, params);

      if (!thread) {
        return null;
      }

      return {
        id: thread.id,
        resourceId: thread.resourceId,
        title: thread.title,
        metadata: typeof thread.metadata === 'string' ? JSON.parse(thread.metadata) : thread.metadata,
        createdAt: thread.createdAtZ || thread.createdAt,
        updatedAt: thread.updatedAtZ || thread.updatedAt,
      };
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'GET_THREAD_BY_ID', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId,
          },
        },
        error,
      );
    }
  }

  /**
   * Atomically reassign a thread and all of its messages to a different resource.
   *
   * Runs inside a single transaction and takes a `SELECT ... FOR UPDATE` row lock on the
   * thread, so overlapping transfers of the same thread serialize and can never interleave
   * the thread update with the message update. Either both the thread and every message move
   * to the new resource, or neither does — there is no split-ownership window. The thread's
   * `createdAt` is preserved. Callers are responsible for authorizing the reassignment.
   */
  async updateThreadResourceId({
    threadId,
    resourceId,
  }: {
    threadId: string;
    resourceId: string;
  }): Promise<StorageThreadType> {
    const threadsTable = getTableName({ indexName: TABLE_THREADS, schemaName: getSchemaName(this.#schema) });
    const messagesTable = getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) });

    try {
      return await this.#db.client.tx(async t => {
        // Lock the thread row for the duration of the transaction. Concurrent transfers of the
        // same thread block here until this transaction commits, so they cannot interleave.
        const thread = await t.oneOrNone<StorageThreadType & { createdAtZ: Date; updatedAtZ: Date }>(
          `SELECT * FROM ${threadsTable} WHERE id = $1 FOR UPDATE`,
          [threadId],
        );

        if (!thread) {
          throw new Error(`Thread "${threadId}" not found`);
        }

        const normalized: StorageThreadType = {
          id: thread.id,
          resourceId: thread.resourceId,
          title: thread.title,
          metadata: typeof thread.metadata === 'string' ? JSON.parse(thread.metadata) : thread.metadata,
          createdAt: thread.createdAtZ || thread.createdAt,
          updatedAt: thread.updatedAtZ || thread.updatedAt,
        };

        if (thread.resourceId === resourceId) {
          return normalized;
        }

        await t.none(
          `UPDATE ${threadsTable} SET "resourceId" = $1, "updatedAt" = NOW(), "updatedAtZ" = NOW() WHERE id = $2`,
          [resourceId, threadId],
        );
        await t.none(`UPDATE ${messagesTable} SET "resourceId" = $1 WHERE thread_id = $2`, [resourceId, threadId]);

        return { ...normalized, resourceId, updatedAt: new Date() };
      });
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'UPDATE_THREAD_RESOURCE_ID', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { threadId, resourceId },
        },
        error,
      );
    }
  }

  public async listThreads(args: StorageListThreadsInput): Promise<StorageListThreadsOutput> {
    const { page = 0, perPage: perPageInput, orderBy, filter } = args;

    try {
      // Validate pagination input before normalization
      // This ensures page === 0 when perPageInput === false
      this.validatePaginationInput(page, perPageInput ?? 100);
    } catch (error) {
      throw new MastraError({
        id: createStorageErrorId('PG', 'LIST_THREADS', 'INVALID_PAGE'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.USER,
        text: error instanceof Error ? error.message : 'Invalid pagination parameters',
        details: { page, ...(perPageInput !== undefined && { perPage: perPageInput }) },
      });
    }

    const perPage = normalizePerPage(perPageInput, 100);

    // Validate metadata keys to prevent SQL injection
    try {
      this.validateMetadataKeys(filter?.metadata);
    } catch (error) {
      throw new MastraError({
        id: createStorageErrorId('PG', 'LIST_THREADS', 'INVALID_METADATA_KEY'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.USER,
        text: error instanceof Error ? error.message : 'Invalid metadata key',
        details: { metadataKeys: filter?.metadata ? Object.keys(filter.metadata).join(', ') : '' },
      });
    }

    const { field, direction } = this.parseOrderBy(orderBy);
    const { offset, perPage: perPageForResponse } = calculatePagination(page, perPageInput, perPage);

    try {
      const tableName = getTableName({ indexName: TABLE_THREADS, schemaName: getSchemaName(this.#schema) });
      const whereClauses: string[] = [];
      const queryParams: any[] = [];
      let paramIndex = 1;

      // Add resourceId filter if provided
      if (filter?.resourceId) {
        whereClauses.push(`"resourceId" = $${paramIndex}`);
        queryParams.push(filter.resourceId);
        paramIndex++;
      }

      // Add metadata filters if provided (AND logic)
      // Uses JSONB containment (@>) to avoid SQL injection and correctly match all value types including null
      // metadata column is TEXT type storing JSON, so we need to cast to jsonb first
      if (filter?.metadata && Object.keys(filter.metadata).length > 0) {
        for (const [key, value] of Object.entries(filter.metadata)) {
          // Use JSONB containment operator - no key interpolation needed
          whereClauses.push(`metadata::jsonb @> $${paramIndex}::jsonb`);
          // Build a small JSON object for each key-value pair
          queryParams.push(JSON.stringify({ [key]: value }));
          paramIndex++;
        }
      }

      const whereClause = whereClauses.length > 0 ? `WHERE ${whereClauses.join(' AND ')}` : '';
      const baseQuery = `FROM ${tableName} ${whereClause}`;

      const countQuery = `SELECT COUNT(*) ${baseQuery}`;
      const countResult = await this.#db.readClient.one(countQuery, queryParams);
      const total = parseInt(countResult.count, 10);

      if (total === 0) {
        return {
          threads: [],
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      const limitValue = perPageInput === false ? total : perPage;
      // Select both standard and timezone-aware columns (*Z) for proper UTC timestamp handling
      const dataQuery = `SELECT id, "resourceId", title, metadata, "createdAt", "createdAtZ", "updatedAt", "updatedAtZ" ${baseQuery} ORDER BY COALESCE("${field}Z", "${field}") ${direction} LIMIT $${paramIndex} OFFSET $${paramIndex + 1}`;
      const rows = await this.#db.readClient.manyOrNone<StorageThreadType & { createdAtZ: Date; updatedAtZ: Date }>(
        dataQuery,
        [...queryParams, limitValue, offset],
      );

      const threads = (rows || []).map(thread => ({
        id: thread.id,
        resourceId: thread.resourceId,
        title: thread.title,
        metadata: typeof thread.metadata === 'string' ? JSON.parse(thread.metadata) : thread.metadata,
        // Use timezone-aware columns (*Z) for correct UTC timestamps, with fallback for legacy data
        createdAt: thread.createdAtZ || thread.createdAt,
        updatedAt: thread.updatedAtZ || thread.updatedAt,
      }));

      return {
        threads,
        total,
        page,
        perPage: perPageForResponse,
        hasMore: perPageInput === false ? false : offset + perPage < total,
      };
    } catch (error) {
      // Re-throw USER errors (validation errors) directly so callers get proper 400 responses
      if (error instanceof MastraError && error.category === ErrorCategory.USER) {
        throw error;
      }
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('PG', 'LIST_THREADS', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            ...(filter?.resourceId && { resourceId: filter.resourceId }),
            hasMetadataFilter: !!filter?.metadata,
            page,
          },
        },
        error,
      );
      this.logger?.error?.(mastraError.toString());
      this.logger?.trackException(mastraError);
      throw mastraError;
    }
  }

  async saveThread({ thread }: { thread: StorageThreadType }): Promise<StorageThreadType> {
    try {
      const tableName = getTableName({ indexName: TABLE_THREADS, schemaName: getSchemaName(this.#schema) });
      const createdAt = toUtcISOString(thread.createdAt);
      const updatedAt = toUtcISOString(thread.updatedAt);
      await this.#db.client.none(
        `INSERT INTO ${tableName} (
          id,
          "resourceId",
          title,
          metadata,
          "createdAt",
          "createdAtZ",
          "updatedAt",
          "updatedAtZ"
        ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8)
        ON CONFLICT (id) DO UPDATE SET
          "resourceId" = EXCLUDED."resourceId",
          title = EXCLUDED.title,
          metadata = EXCLUDED.metadata,
          "createdAt" = EXCLUDED."createdAt",
          "createdAtZ" = EXCLUDED."createdAtZ",
          "updatedAt" = EXCLUDED."updatedAt",
          "updatedAtZ" = EXCLUDED."updatedAtZ"`,
        [
          thread.id,
          thread.resourceId,
          thread.title,
          thread.metadata ? JSON.stringify(thread.metadata) : null,
          createdAt,
          createdAt,
          updatedAt,
          updatedAt,
        ],
      );

      return thread;
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'SAVE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId: thread.id,
          },
        },
        error,
      );
    }
  }

  async updateThread({
    id,
    title,
    metadata,
  }: {
    id: string;
    title?: string;
    metadata?: Record<string, unknown>;
  }): Promise<StorageThreadType> {
    const threadTableName = getTableName({ indexName: TABLE_THREADS, schemaName: getSchemaName(this.#schema) });
    const existingThread = await this.#getThreadById(this.#db.client, { threadId: id });
    if (!existingThread) {
      throw new MastraError({
        id: createStorageErrorId('PG', 'UPDATE_THREAD', 'FAILED'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.USER,
        text: `Thread ${id} not found`,
        details: {
          threadId: id,
          title: title ?? null,
        },
      });
    }

    const mergedMetadata = {
      ...existingThread.metadata,
      ...metadata,
    };

    try {
      const now = new Date();
      const nowStr = toUtcISOString(now);
      const thread = await this.#db.client.one<StorageThreadType & { createdAtZ: Date; updatedAtZ: Date }>(
        `UPDATE ${threadTableName}
                    SET
                        title = COALESCE($1, title),
                        metadata = $2,
                        "updatedAt" = $3,
                        "updatedAtZ" = $4
                    WHERE id = $5
                    RETURNING *
                `,
        [title ?? null, mergedMetadata, nowStr, nowStr, id],
      );

      return {
        id: thread.id,
        resourceId: thread.resourceId,
        title: thread.title,
        metadata: typeof thread.metadata === 'string' ? JSON.parse(thread.metadata) : thread.metadata,
        createdAt: thread.createdAtZ || thread.createdAt,
        updatedAt: thread.updatedAtZ || thread.updatedAt,
      };
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'UPDATE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId: id,
            title: title ?? null,
          },
        },
        error,
      );
    }
  }

  async deleteThread({ threadId }: { threadId: string }): Promise<void> {
    try {
      const tableName = getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) });
      const threadTableName = getTableName({ indexName: TABLE_THREADS, schemaName: getSchemaName(this.#schema) });
      await this.#db.client.tx(async t => {
        await t.none(`DELETE FROM ${tableName} WHERE thread_id = $1`, [threadId]);

        const schemaName = this.#schema || 'public';
        const vectorTables = await t.manyOrNone<{ tablename: string }>(
          `
          SELECT tablename
          FROM pg_tables
          WHERE schemaname = $1
          AND (tablename = 'memory_messages' OR tablename LIKE 'memory_messages_%')
        `,
          [schemaName],
        );

        for (const { tablename } of vectorTables) {
          const vectorTableName = getTableName({ indexName: tablename, schemaName: getSchemaName(this.#schema) });
          await t.none(`DELETE FROM ${vectorTableName} WHERE metadata->>'thread_id' = $1`, [threadId]);
        }

        await t.none(`DELETE FROM ${threadTableName} WHERE id = $1`, [threadId]);
      });
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'DELETE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId,
          },
        },
        error,
      );
    }
  }

  /**
   * Fetches messages around target messages using cursor-based pagination.
   *
   * This replaces the previous ROW_NUMBER() approach which caused severe performance
   * issues on large tables (see GitHub issue #11150). The old approach required
   * scanning and sorting ALL messages in a thread to assign row numbers.
   *
   * The current approach uses two phases for optimal performance:
   * 1. Batch-fetch all target messages' metadata (thread_id, createdAt) in one query
   * 2. Build cursor subqueries using "createdAt" directly (not COALESCE) so that
   *    the existing (thread_id, createdAt DESC) index can be used for index scans
   *    instead of sequential scans. This fixes GitHub issue #11702 where semantic
   *    recall latency scaled linearly with message count (~30s for 7.4k messages).
   */
  private _sortMessages(messages: MastraDBMessage[], field: string, direction: string): MastraDBMessage[] {
    return messages.sort((a, b) => {
      const aValue = field === 'createdAt' ? new Date(a.createdAt).getTime() : (a as any)[field];
      const bValue = field === 'createdAt' ? new Date(b.createdAt).getTime() : (b as any)[field];

      if (aValue == null && bValue == null) return a.id.localeCompare(b.id);
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (aValue === bValue) {
        return a.id.localeCompare(b.id);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'ASC' ? aValue - bValue : bValue - aValue;
      }
      return direction === 'ASC'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }

  /**
   * Fetches included messages by ID, discovering their thread automatically.
   * This handles cross-thread includes where the include item doesn't specify a threadId.
   * When a resourceId is given, both the target lookup and the surrounding window stay
   * inside that resource, so an include never leaks another resource's messages.
   */
  private async _getIncludedMessages({
    include,
    resourceId,
  }: {
    include: StorageListMessagesInput['include'];
    resourceId?: string;
  }) {
    if (!include || include.length === 0) return null;

    const tableName = getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) });
    const selectColumns = `id, content, role, type, "createdAt", "createdAtZ", thread_id AS "threadId", "resourceId"`;

    // Phase 1: Batch-fetch metadata for all target messages in a single query.
    // This eliminates the correlated subselects that previously ran per-subquery.
    const targetIds = include.map(inc => inc.id).filter(Boolean);
    if (targetIds.length === 0) return null;

    const idPlaceholders = targetIds.map((_, i) => '$' + (i + 1)).join(', ');
    const targetResourceCondition = resourceId ? ` AND "resourceId" = $${targetIds.length + 1}` : '';
    const targetRows = await this.#db.readClient.manyOrNone<{
      id: string;
      thread_id: string;
      createdAt: Date | string;
    }>(
      `SELECT id, thread_id, "createdAt" FROM ${tableName} WHERE id IN (${idPlaceholders})${targetResourceCondition}`,
      resourceId ? [...targetIds, resourceId] : targetIds,
    );

    if (targetRows.length === 0) return null;

    const targetMap = new Map(targetRows.map(r => [r.id, { threadId: r.thread_id, createdAt: r.createdAt }]));

    // Phase 2: Build cursor subqueries using materialized constants from Phase 1.
    // Uses "createdAt" directly instead of COALESCE("createdAtZ", "createdAt") so
    // the (thread_id, createdAt DESC) composite index covers the query.
    // createdAt and createdAtZ always store the same instant (createdAtZ is a TIMESTAMPTZ
    // copy for timezone-correctness), so using createdAt for ordering is safe.
    const unionQueries: string[] = [];
    const params: any[] = [];
    // resourceId is the same for every subquery, so bind it once as $1 and reference
    // that placeholder from each subquery instead of re-binding it per include item.
    let resourceCondition = '';
    if (resourceId) {
      params.push(resourceId);
      resourceCondition = ` AND m."resourceId" = $1`;
    }
    let paramIdx = params.length + 1;

    for (const inc of include) {
      const { id, withPreviousMessages = 0, withNextMessages = 0 } = inc;
      const target = targetMap.get(id);
      if (!target) continue;

      // Fetch the target message itself plus previous messages.
      // Uses createdAt <= target's createdAt, ordered DESC, limited to withPreviousMessages + 1
      const p1 = '$' + paramIdx;
      const p2 = '$' + (paramIdx + 1);
      const p3 = '$' + (paramIdx + 2);
      unionQueries.push(`(
        SELECT ${selectColumns}
        FROM ${tableName} m
        WHERE m.thread_id = ${p1}
          AND m."createdAt" <= ${p2}${resourceCondition}
        ORDER BY m."createdAt" DESC, m.id DESC
        LIMIT ${p3}
      )`);
      params.push(target.threadId, target.createdAt, withPreviousMessages + 1);
      paramIdx += 3;

      // Fetch messages after the target (only if requested)
      if (withNextMessages > 0) {
        const p4 = '$' + paramIdx;
        const p5 = '$' + (paramIdx + 1);
        const p6 = '$' + (paramIdx + 2);
        unionQueries.push(`(
          SELECT ${selectColumns}
          FROM ${tableName} m
          WHERE m.thread_id = ${p4}
            AND m."createdAt" > ${p5}${resourceCondition}
          ORDER BY m."createdAt" ASC, m.id ASC
          LIMIT ${p6}
        )`);
        params.push(target.threadId, target.createdAt, withNextMessages);
        paramIdx += 3;
      }
    }

    if (unionQueries.length === 0) return null;

    // When there's only one subquery, we don't need UNION ALL or an outer ORDER BY
    // (the subquery already has its own ORDER BY)
    // When there are multiple subqueries, we join them and sort the combined result
    let finalQuery: string;
    if (unionQueries.length === 1) {
      // Single query - just use it directly (remove outer parentheses for cleaner SQL)
      finalQuery = unionQueries[0]!.slice(1, -1); // Remove ( and )
    } else {
      // Multiple queries - UNION ALL and sort the result
      finalQuery = `SELECT * FROM (${unionQueries.join(' UNION ALL ')}) AS combined ORDER BY "createdAt" ASC, id ASC`;
    }
    const includedRows = await this.#db.readClient.manyOrNone(finalQuery, params);

    // Deduplicate results (messages may appear in multiple context windows)
    const seen = new Set<string>();
    const dedupedRows = includedRows.filter(row => {
      if (seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    });
    return dedupedRows;
  }

  private parseRow(row: MessageRowFromDB): MastraDBMessage {
    const normalized = this.normalizeMessageRow(row);
    let content = normalized.content;
    try {
      content = JSON.parse(normalized.content);
    } catch {
      // use content as is if it's not JSON
    }
    return {
      id: normalized.id,
      content,
      role: normalized.role as MastraDBMessage['role'],
      createdAt: new Date(normalized.createdAt as string),
      threadId: normalized.threadId,
      resourceId: normalized.resourceId,
      ...(normalized.type && normalized.type !== 'v2' ? { type: normalized.type } : {}),
    } satisfies MastraDBMessage;
  }

  public async listMessagesById({ messageIds }: { messageIds: string[] }): Promise<{ messages: MastraDBMessage[] }> {
    if (messageIds.length === 0) return { messages: [] };
    const selectStatement = `SELECT id, content, role, type, "createdAt", "createdAtZ", thread_id AS "threadId", "resourceId"`;

    try {
      const tableName = getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) });
      const query = `
        ${selectStatement} FROM ${tableName}
        WHERE id IN (${inPlaceholders(messageIds.length)})
        ORDER BY "createdAt" DESC
      `;
      const resultRows = await this.#db.readClient.manyOrNone(query, messageIds);

      const list = new MessageList().add(
        resultRows.map(row => this.parseRow(row)) as (MastraMessageV1 | MastraDBMessage)[],
        'memory',
      );
      return { messages: list.get.all.db() };
    } catch (error) {
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('PG', 'LIST_MESSAGES_BY_ID', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            messageIds: JSON.stringify(messageIds),
          },
        },
        error,
      );
      this.logger?.error?.(mastraError.toString());
      this.logger?.trackException(mastraError);
      throw mastraError;
    }
  }

  /**
   * Reads one page of messages together with the total row count.
   *
   * Every page query carries a skinny scalar `(SELECT COUNT(*) ...)` subquery that
   * reports the total over the whole WHERE result on the same statement as the page,
   * so the page costs one database round-trip instead of two. The page and the count
   * also come from one snapshot, so the count always describes the returned rows. A
   * separate `COUNT(*)` runs only as a fallback when the page is empty and the caller
   * asked for a page after the last row, because there is then no row to carry the
   * count on.
   */
  async #fetchMessagePage({
    selectStatement,
    tableName,
    whereClause,
    orderByStatement,
    queryParams,
    perPageInput,
    perPage,
    offset,
    includeTotal = true,
  }: {
    selectStatement: string;
    tableName: string;
    whereClause: string;
    orderByStatement: string;
    queryParams: any[];
    perPageInput: number | false | undefined;
    perPage: number;
    offset: number;
    includeTotal?: boolean;
  }): Promise<{ total: number; messages: MessageRowFromDB[]; hasMore?: boolean }> {
    // When the caller does not need `total` (e.g. agent last-N reads), skip the
    // `COUNT(*)` subquery entirely. For a bounded page we peek one extra row
    // (LIMIT perPage + 1) so `hasMore` can be derived without counting the whole
    // thread. `total` is not a real count in this path, so callers must not use it.
    if (includeTotal === false && perPageInput !== false) {
      const peekLimit = perPage + 1;
      const rows =
        (await this.#db.readClient.manyOrNone<MessageRowFromDB>(
          `${selectStatement} FROM ${tableName} ${whereClause} ${orderByStatement} LIMIT $${queryParams.length + 1} OFFSET $${queryParams.length + 2}`,
          [...queryParams, peekLimit, offset],
        )) || [];
      const hasMore = rows.length > perPage;
      const messages = hasMore ? rows.slice(0, perPage) : rows;
      return { total: offset + messages.length, messages, hasMore };
    }

    // `perPageInput === false` means "every row", so no LIMIT is applied.
    const limitClause =
      perPageInput === false ? '' : ` LIMIT $${queryParams.length + 1} OFFSET $${queryParams.length + 2}`;
    const dataParams = perPageInput === false ? queryParams : [...queryParams, perPage, offset];

    if (includeTotal === false) {
      // Unbounded read (perPageInput === false) that does not need `total`: skip the
      // COUNT(*) subquery. Every matching row is returned, so `hasMore` is false.
      const rows =
        (await this.#db.readClient.manyOrNone<MessageRowFromDB>(
          `${selectStatement} FROM ${tableName} ${whereClause} ${orderByStatement}`,
          dataParams,
        )) || [];
      return { total: rows.length, messages: rows, hasMore: false };
    }

    // Use a skinny scalar COUNT(*) subquery rather than COUNT(*) OVER (). The window ran over
    // the full SELECT list, forcing Postgres to materialize/heap-fetch `content` for every
    // matching row before LIMIT. The uncorrelated subquery is evaluated once (InitPlan) and
    // can use an index-only scan, while the outer SELECT is served by an index scan + LIMIT.
    // Both WHERE clauses reference the same positional params, so `dataParams` is unchanged.
    const rows =
      (await this.#db.readClient.manyOrNone<MessageRowFromDB & { __total?: string | number }>(
        `${selectStatement}, (SELECT COUNT(*) FROM ${tableName} ${whereClause}) AS "__total" FROM ${tableName} ${whereClause} ${orderByStatement}${limitClause}`,
        dataParams,
      )) || [];

    if (rows.length > 0) {
      return { total: Number(rows[0]!.__total), messages: rows };
    }
    if (offset === 0) {
      return { total: 0, messages: [] };
    }
    const countResult = await this.#db.readClient.one(`SELECT COUNT(*) FROM ${tableName} ${whereClause}`, queryParams);
    return { total: parseInt(countResult.count, 10), messages: [] };
  }

  public async listMessages(args: StorageListMessagesInput): Promise<StorageListMessagesOutput> {
    const {
      threadId,
      resourceId,
      include,
      filter,
      perPage: perPageInput,
      page = 0,
      orderBy,
      includeTotal = true,
    } = args;

    const threadIds = (Array.isArray(threadId) ? threadId : [threadId]).filter(
      (id): id is string => typeof id === 'string',
    );

    if (threadIds.length === 0 || threadIds.some(id => !id.trim())) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'LIST_MESSAGES', 'INVALID_THREAD_ID'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { threadId: Array.isArray(threadId) ? String(threadId) : String(threadId) },
        },
        new Error('threadId must be a non-empty string or array of non-empty strings'),
      );
    }

    if (page < 0) {
      throw new MastraError({
        id: createStorageErrorId('PG', 'LIST_MESSAGES', 'INVALID_PAGE'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.USER,
        text: 'Page number must be non-negative',
        details: {
          threadId: Array.isArray(threadId) ? threadId.join(',') : threadId,
          page,
        },
      });
    }

    const perPage = normalizePerPage(perPageInput, 40);
    const { offset, perPage: perPageForResponse } = calculatePagination(page, perPageInput, perPage);
    const metadataFilter = validateStorageMetadataFilter(filter?.metadata);

    try {
      const { field, direction } = this.parseOrderBy(orderBy, 'ASC');
      // Order by the raw timestamp column (not COALESCE("${field}Z", "${field}")) so the
      // (thread_id, createdAt DESC) composite index can serve the LIMIT with an index scan
      // instead of materializing/seq-scanning the whole thread. createdAt and createdAtZ
      // always store the same instant (createdAtZ is a TIMESTAMPTZ copy), so row selection
      // under LIMIT is identical. This mirrors the index-safe ordering in _getIncludedMessages.
      const orderByStatement = `ORDER BY "${field}" ${direction}`;

      const selectStatement = `SELECT id, content, role, type, "createdAt", "createdAtZ", thread_id AS "threadId", "resourceId"`;
      const tableName = getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) });

      const conditions: string[] = [`thread_id IN (${inPlaceholders(threadIds.length)})`];
      const queryParams: any[] = [...threadIds];
      let paramIndex = threadIds.length + 1;

      if (resourceId) {
        conditions.push(`"resourceId" = $${paramIndex++}`);
        queryParams.push(resourceId);
      }

      if (filter?.dateRange?.start) {
        const startOp = filter.dateRange.startExclusive ? '>' : '>=';
        conditions.push(`COALESCE("createdAtZ", "createdAt") ${startOp} $${paramIndex++}`);
        queryParams.push(filter.dateRange.start);
      }

      if (filter?.dateRange?.end) {
        const endOp = filter.dateRange.endExclusive ? '<' : '<=';
        conditions.push(`COALESCE("createdAtZ", "createdAt") ${endOp} $${paramIndex++}`);
        queryParams.push(filter.dateRange.end);
      }

      const whereClause = conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : '';

      // When perPage is 0 with no includes, there's nothing to return.
      if (perPage === 0 && (!include || include.length === 0)) {
        return { messages: [], total: 0, page, perPage: perPageForResponse, hasMore: false };
      }

      // When perPage is 0 and we have include targets, skip COUNT(*) and data queries.
      // This is the semantic recall path where we only need the included messages.
      if (perPage === 0 && include && include.length > 0) {
        const includeMessages = await this._getIncludedMessages({ include, resourceId });
        if (!includeMessages || includeMessages.length === 0) {
          return { messages: [], total: 0, page, perPage: perPageForResponse, hasMore: false };
        }
        const messagesWithParsedContent = includeMessages.map(row => this.parseRow(row));
        const list = new MessageList().add(messagesWithParsedContent, 'memory');
        return {
          messages: this._sortMessages(list.get.all.db(), field, direction),
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      // The included messages do not depend on the page, so start that read now and
      // let it overlap the page read. The rejection is captured here so a failure of
      // the page read cannot leave this promise unhandled.
      let includeFailure: unknown;
      const includePromise =
        include && include.length > 0
          ? this._getIncludedMessages({ include, resourceId }).catch((error: unknown) => {
              includeFailure = error;
              return null;
            })
          : null;

      let total: number;
      let messages: MessageRowFromDB[];
      let peekedHasMore: boolean | undefined;
      if (metadataFilter) {
        const rows = await this.#db.readClient.manyOrNone(
          `${selectStatement} FROM ${tableName} ${whereClause} ${orderByStatement}`,
          queryParams,
        );
        const filteredRows = (rows || []).filter(row =>
          storageMessageMatchesMetadataFilter(row.content, metadataFilter),
        );
        total = filteredRows.length;
        messages = perPageInput === false ? filteredRows : filteredRows.slice(offset, offset + perPage);
      } else {
        const pageResult = await this.#fetchMessagePage({
          selectStatement,
          tableName,
          whereClause,
          orderByStatement,
          queryParams,
          perPageInput,
          perPage,
          offset,
          includeTotal,
        });
        total = pageResult.total;
        messages = pageResult.messages;
        peekedHasMore = pageResult.hasMore;
      }
      const primaryPageCount = messages.length;

      if (total === 0 && messages.length === 0 && (!include || include.length === 0)) {
        return {
          messages: [],
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      const messageIds = new Set(messages.map(m => m.id));
      if (include && include.length > 0) {
        const includeMessages = await includePromise;
        if (includeFailure) throw includeFailure;
        if (includeMessages) {
          for (const includeMsg of includeMessages) {
            if (!messageIds.has(includeMsg.id)) {
              messages.push(includeMsg);
              messageIds.add(includeMsg.id);
            }
          }
        }
      }

      const messagesWithParsedContent = messages.map(row => this.parseRow(row));

      const list = new MessageList().add(messagesWithParsedContent, 'memory');
      const finalMessages = this._sortMessages(list.get.all.db(), field, direction);

      const threadIdSet = new Set(threadIds);
      const returnedThreadMessageIds = new Set(
        finalMessages.filter(m => m.threadId && threadIdSet.has(m.threadId)).map(m => m.id),
      );
      const allThreadMessagesReturned = returnedThreadMessageIds.size >= total;
      const hasMore =
        peekedHasMore !== undefined
          ? peekedHasMore
          : metadataFilter
            ? perPageInput !== false && offset + primaryPageCount < total
            : perPageInput !== false && !allThreadMessagesReturned && offset + perPage < total;

      return {
        messages: finalMessages,
        total,
        page,
        perPage: perPageForResponse,
        hasMore,
      };
    } catch (error) {
      // Re-throw USER errors (validation errors) directly so callers get proper 400 responses
      if (error instanceof MastraError && error.category === ErrorCategory.USER) {
        throw error;
      }
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('PG', 'LIST_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId: Array.isArray(threadId) ? threadId.join(',') : threadId,
            resourceId: resourceId ?? '',
          },
        },
        error,
      );
      this.logger?.error?.(mastraError.toString());
      this.logger?.trackException(mastraError);
      throw mastraError;
    }
  }

  public async listMessagesByResourceId(
    args: StorageListMessagesByResourceIdInput,
  ): Promise<StorageListMessagesOutput> {
    const { resourceId, include, filter, perPage: perPageInput, page = 0, orderBy, includeTotal = true } = args;

    // Validate that resourceId is provided
    const hasResourceId = resourceId !== undefined && resourceId !== null && resourceId.trim() !== '';
    if (!hasResourceId) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'LIST_MESSAGES_BY_RESOURCE_ID', 'INVALID_QUERY'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.USER,
          details: {
            resourceId: resourceId ?? '',
          },
        },
        new Error('resourceId is required'),
      );
    }

    // Validate page parameter
    if (page < 0) {
      throw new MastraError({
        id: createStorageErrorId('PG', 'LIST_MESSAGES_BY_RESOURCE_ID', 'INVALID_PAGE'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.USER,
        text: 'Page number must be non-negative',
        details: {
          resourceId,
          page,
        },
      });
    }

    const perPage = normalizePerPage(perPageInput, 40);
    const { offset, perPage: perPageForResponse } = calculatePagination(page, perPageInput, perPage);
    const metadataFilter = validateStorageMetadataFilter(filter?.metadata);

    try {
      const { field, direction } = this.parseOrderBy(orderBy, 'ASC');
      // Order by the raw timestamp column (not COALESCE("${field}Z", "${field}")) so the
      // (thread_id, createdAt DESC) composite index can serve the LIMIT with an index scan
      // instead of materializing/seq-scanning the whole thread. createdAt and createdAtZ
      // always store the same instant (createdAtZ is a TIMESTAMPTZ copy), so row selection
      // under LIMIT is identical. This mirrors the index-safe ordering in _getIncludedMessages.
      const orderByStatement = `ORDER BY "${field}" ${direction}`;

      const selectStatement = `SELECT id, content, role, type, "createdAt", "createdAtZ", thread_id AS "threadId", "resourceId"`;
      const tableName = getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) });

      // Build WHERE conditions
      const conditions: string[] = [];
      const queryParams: any[] = [];
      let paramIndex = 1;

      // Add resourceId filter
      conditions.push(`"resourceId" = $${paramIndex++}`);
      queryParams.push(resourceId);

      if (filter?.dateRange?.start) {
        const startOp = filter.dateRange.startExclusive ? '>' : '>=';
        conditions.push(`COALESCE("createdAtZ", "createdAt") ${startOp} $${paramIndex++}`);
        queryParams.push(filter.dateRange.start);
      }

      if (filter?.dateRange?.end) {
        const endOp = filter.dateRange.endExclusive ? '<' : '<=';
        conditions.push(`COALESCE("createdAtZ", "createdAt") ${endOp} $${paramIndex++}`);
        queryParams.push(filter.dateRange.end);
      }

      const whereClause = conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : '';

      // When perPage is 0 with no includes, there's nothing to return.
      if (perPage === 0 && (!include || include.length === 0)) {
        return { messages: [], total: 0, page, perPage: perPageForResponse, hasMore: false };
      }

      // Fast path: when perPage is 0 and include is provided, skip COUNT(*) and the
      // main data query entirely. This is the semantic recall path where only included
      // (vector-matched) messages are needed. Skipping the COUNT(*) avoids scanning
      // the entire thread which was a major source of latency for large threads.
      if (perPage === 0 && include && include.length > 0) {
        const includeMessages = await this._getIncludedMessages({ include, resourceId });
        if (!includeMessages || includeMessages.length === 0) {
          return {
            messages: [],
            total: 0,
            page,
            perPage: perPageForResponse,
            hasMore: false,
          };
        }

        const messagesWithParsedContent = includeMessages.map(row => this.parseRow(row));
        const list = new MessageList().add(messagesWithParsedContent, 'memory');

        return {
          messages: this._sortMessages(list.get.all.db(), field, direction),
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      // The included messages do not depend on the page, so start that read now and
      // let it overlap the page read. The rejection is captured here so a failure of
      // the page read cannot leave this promise unhandled.
      let includeFailure: unknown;
      const includePromise =
        include && include.length > 0
          ? this._getIncludedMessages({ include, resourceId }).catch((error: unknown) => {
              includeFailure = error;
              return null;
            })
          : null;

      let total: number;
      let messages: MessageRowFromDB[];
      let peekedHasMore: boolean | undefined;
      if (metadataFilter) {
        const rows = await this.#db.readClient.manyOrNone(
          `${selectStatement} FROM ${tableName} ${whereClause} ${orderByStatement}`,
          queryParams,
        );
        const filteredRows = (rows || []).filter(row =>
          storageMessageMatchesMetadataFilter(row.content, metadataFilter),
        );
        total = filteredRows.length;
        messages = perPageInput === false ? filteredRows : filteredRows.slice(offset, offset + perPage);
      } else {
        const pageResult = await this.#fetchMessagePage({
          selectStatement,
          tableName,
          whereClause,
          orderByStatement,
          queryParams,
          perPageInput,
          perPage,
          offset,
          includeTotal,
        });
        total = pageResult.total;
        messages = pageResult.messages;
        peekedHasMore = pageResult.hasMore;
      }

      if (total === 0 && messages.length === 0 && (!include || include.length === 0)) {
        return {
          messages: [],
          total: 0,
          page,
          perPage: perPageForResponse,
          hasMore: false,
        };
      }

      const messageIds = new Set(messages.map(m => m.id));
      if (include && include.length > 0) {
        const includeMessages = await includePromise;
        if (includeFailure) throw includeFailure;
        if (includeMessages) {
          for (const includeMsg of includeMessages) {
            if (!messageIds.has(includeMsg.id)) {
              messages.push(includeMsg);
              messageIds.add(includeMsg.id);
            }
          }
        }
      }

      const messagesWithParsedContent = messages.map(row => this.parseRow(row));

      const list = new MessageList().add(messagesWithParsedContent, 'memory');
      const finalMessages = this._sortMessages(list.get.all.db(), field, direction);

      const hasMore = peekedHasMore !== undefined ? peekedHasMore : perPageInput !== false && offset + perPage < total;

      return {
        messages: finalMessages,
        total,
        page,
        perPage: perPageForResponse,
        hasMore,
      };
    } catch (error) {
      // Re-throw USER errors (validation errors) directly so callers get proper 400 responses
      if (error instanceof MastraError && error.category === ErrorCategory.USER) {
        throw error;
      }
      const mastraError = new MastraError(
        {
          id: createStorageErrorId('PG', 'LIST_MESSAGES_BY_RESOURCE_ID', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            resourceId: resourceId ?? '',
          },
        },
        error,
      );
      this.logger?.error?.(mastraError.toString());
      this.logger?.trackException(mastraError);
      throw mastraError;
    }
  }

  async saveMessages({ messages }: { messages: MastraDBMessage[] }): Promise<{ messages: MastraDBMessage[] }> {
    if (messages.length === 0) return { messages: [] };

    const threadId = messages[0]?.threadId;
    if (!threadId) {
      throw new MastraError({
        id: createStorageErrorId('PG', 'SAVE_MESSAGES', 'FAILED'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.THIRD_PARTY,
        text: `Thread ID is required`,
      });
    }

    try {
      const tableName = getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) });
      const threadIds = new Set<string>();
      for (const message of messages) {
        if (!message.threadId) {
          throw new Error(
            `Expected to find a threadId for message, but couldn't find one. An unexpected error has occurred.`,
          );
        }
        if (!message.resourceId) {
          throw new Error(
            `Expected to find a resourceId for message, but couldn't find one. An unexpected error has occurred.`,
          );
        }
        threadIds.add(message.threadId);
      }

      for (const threadIdToCheck of threadIds) {
        const thread = await this.#getThreadById(this.#db.client, { threadId: threadIdToCheck });
        if (!thread) {
          throw new MastraError({
            id: createStorageErrorId('PG', 'SAVE_MESSAGES', 'FAILED'),
            domain: ErrorDomain.STORAGE,
            category: ErrorCategory.THIRD_PARTY,
            text: `Thread ${threadIdToCheck} not found`,
            details: {
              threadId: threadIdToCheck,
            },
          });
        }
      }

      const messagesToSave = dedupeMessagesForSave(messages);
      await this.#db.client.tx(async t => {
        for (let offset = 0; offset < messagesToSave.length; offset += MAX_MESSAGES_PER_INSERT) {
          const batch = messagesToSave.slice(offset, offset + MAX_MESSAGES_PER_INSERT);
          const values: unknown[] = [];
          const valuePlaceholders = batch
            .map((message, messageIndex) => {
              const createdAt = toUtcISOString(message.createdAt || new Date());
              values.push(
                message.id,
                message.threadId,
                typeof message.content === 'string' ? message.content : JSON.stringify(message.content),
                createdAt,
                createdAt,
                message.role,
                message.type || 'v2',
                message.resourceId,
              );

              const paramOffset = messageIndex * MESSAGE_INSERT_BIND_PARAMETERS;
              return `(${Array.from(
                { length: MESSAGE_INSERT_BIND_PARAMETERS },
                (_, paramIndex) => `$${paramOffset + paramIndex + 1}`,
              ).join(', ')})`;
            })
            .join(', ');

          await t.none(
            `INSERT INTO ${tableName} (id, thread_id, content, "createdAt", "createdAtZ", role, type, "resourceId")
             VALUES ${valuePlaceholders}
             ON CONFLICT (id) DO UPDATE SET
              thread_id = EXCLUDED.thread_id,
              content = EXCLUDED.content,
              role = EXCLUDED.role,
              type = EXCLUDED.type,
              "resourceId" = EXCLUDED."resourceId"`,
            values,
          );
        }

        const threadTableName = getTableName({ indexName: TABLE_THREADS, schemaName: getSchemaName(this.#schema) });
        const now = toUtcISOString(new Date());
        for (const threadIdToUpdate of threadIds) {
          await t.none(
            `UPDATE ${threadTableName}
              SET
                "updatedAt" = $1,
                "updatedAtZ" = $2
              WHERE id = $3`,
            [now, now, threadIdToUpdate],
          );
        }
      });

      const messagesWithParsedContent = messages.map(message => {
        if (typeof message.content === 'string') {
          try {
            return { ...message, content: JSON.parse(message.content) };
          } catch {
            return message;
          }
        }
        return message;
      });

      const list = new MessageList().add(messagesWithParsedContent as (MastraMessageV1 | MastraDBMessage)[], 'memory');
      return { messages: list.get.all.db() };
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'SAVE_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            threadId,
          },
        },
        error,
      );
    }
  }

  async updateMessages({
    messages,
  }: {
    messages: (Partial<Omit<MastraDBMessage, 'createdAt'>> & {
      id: string;
      content?: {
        metadata?: MastraMessageContentV2['metadata'];
        content?: MastraMessageContentV2['content'];
      };
    })[];
  }): Promise<MastraDBMessage[]> {
    if (messages.length === 0) {
      return [];
    }

    const messageIds = messages.map(m => m.id);

    const selectQuery = `SELECT id, content, role, type, "createdAt", "createdAtZ", thread_id AS "threadId", "resourceId" FROM ${getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) })} WHERE id IN (${inPlaceholders(messageIds.length)})`;

    const existingMessagesDb = await this.#db.client.manyOrNone(selectQuery, messageIds);

    if (existingMessagesDb.length === 0) {
      return [];
    }

    const existingMessages: MastraDBMessage[] = existingMessagesDb.map(msg => {
      if (typeof msg.content === 'string') {
        try {
          msg.content = JSON.parse(msg.content);
        } catch {
          // ignore if not valid json
        }
      }
      return msg as MastraDBMessage;
    });

    const threadIdsToUpdate = new Set<string>();

    await this.#db.client.tx(async t => {
      const queries = [];
      const columnMapping: Record<string, string> = {
        threadId: 'thread_id',
      };

      for (const existingMessage of existingMessages) {
        const updatePayload = messages.find(m => m.id === existingMessage.id);
        if (!updatePayload) continue;

        const { id, ...fieldsToUpdate } = updatePayload;
        if (Object.keys(fieldsToUpdate).length === 0) continue;

        threadIdsToUpdate.add(existingMessage.threadId!);
        if (updatePayload.threadId && updatePayload.threadId !== existingMessage.threadId) {
          threadIdsToUpdate.add(updatePayload.threadId);
        }

        const setClauses: string[] = [];
        const values: any[] = [];
        let paramIndex = 1;

        const updatableFields = { ...fieldsToUpdate };

        if (updatableFields.content) {
          const newContent = {
            ...existingMessage.content,
            ...updatableFields.content,
            ...(existingMessage.content?.metadata && updatableFields.content.metadata
              ? {
                  metadata: {
                    ...existingMessage.content.metadata,
                    ...updatableFields.content.metadata,
                  },
                }
              : {}),
          };
          setClauses.push(`content = $${paramIndex++}`);
          values.push(newContent);
          delete updatableFields.content;
        }

        for (const key in updatableFields) {
          if (Object.prototype.hasOwnProperty.call(updatableFields, key)) {
            const dbColumn = columnMapping[key] || key;
            setClauses.push(`"${dbColumn}" = $${paramIndex++}`);
            values.push(updatableFields[key as keyof typeof updatableFields]);
          }
        }

        if (setClauses.length > 0) {
          values.push(id);
          const sql = `UPDATE ${getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) })} SET ${setClauses.join(', ')} WHERE id = $${paramIndex}`;
          queries.push(t.none(sql, values));
        }
      }

      if (threadIdsToUpdate.size > 0) {
        const threadIds = Array.from(threadIdsToUpdate);
        queries.push(
          t.none(
            `UPDATE ${getTableName({ indexName: TABLE_THREADS, schemaName: getSchemaName(this.#schema) })} SET "updatedAt" = NOW(), "updatedAtZ" = NOW() WHERE id IN (${inPlaceholders(threadIds.length)})`,
            threadIds,
          ),
        );
      }

      if (queries.length > 0) {
        await t.batch(queries);
      }
    });

    const updatedMessages = await this.#db.client.manyOrNone<MessageRowFromDB>(selectQuery, messageIds);

    return (updatedMessages || []).map((row: MessageRowFromDB) => {
      const message = this.normalizeMessageRow(row);
      if (typeof message.content === 'string') {
        try {
          return { ...message, content: JSON.parse(message.content) } as MastraDBMessage;
        } catch {
          /* ignore */
        }
      }
      return message as MastraDBMessage;
    });
  }

  async deleteMessages(messageIds: string[]): Promise<void> {
    if (!messageIds || messageIds.length === 0) {
      return;
    }

    try {
      const messageTableName = getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) });
      const threadTableName = getTableName({ indexName: TABLE_THREADS, schemaName: getSchemaName(this.#schema) });

      await this.#db.client.tx(async t => {
        const placeholders = messageIds.map((_, idx) => `$${idx + 1}`).join(',');
        const messages = await t.manyOrNone(
          `SELECT DISTINCT thread_id FROM ${messageTableName} WHERE id IN (${placeholders})`,
          messageIds,
        );

        const threadIds = messages?.map(msg => msg.thread_id).filter(Boolean) || [];

        await t.none(`DELETE FROM ${messageTableName} WHERE id IN (${placeholders})`, messageIds);

        if (threadIds.length > 0) {
          await t.none(
            `UPDATE ${threadTableName} SET "updatedAt" = NOW(), "updatedAtZ" = NOW() WHERE id IN (${inPlaceholders(threadIds.length)})`,
            threadIds,
          );
        }
      });
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'DELETE_MESSAGES', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { messageIds: messageIds.join(', ') },
        },
        error,
      );
    }
  }

  async getResourceById({ resourceId }: { resourceId: string }): Promise<StorageResourceType | null> {
    return this.#getResourceById(this.#db.readClient, resourceId);
  }

  async #getResourceById(client: DbClient, resourceId: string): Promise<StorageResourceType | null> {
    const tableName = getTableName({ indexName: TABLE_RESOURCES, schemaName: getSchemaName(this.#schema) });
    const result = await client.oneOrNone<StorageResourceType & { createdAtZ: Date; updatedAtZ: Date }>(
      `SELECT * FROM ${tableName} WHERE id = $1`,
      [resourceId],
    );

    if (!result) {
      return null;
    }

    return {
      id: result.id,
      createdAt: result.createdAtZ || result.createdAt,
      updatedAt: result.updatedAtZ || result.updatedAt,
      workingMemory: result.workingMemory,
      metadata: typeof result.metadata === 'string' ? JSON.parse(result.metadata) : result.metadata,
    };
  }

  async saveResource({ resource }: { resource: StorageResourceType }): Promise<StorageResourceType> {
    const createdAt = toUtcISOString(resource.createdAt);
    const updatedAt = toUtcISOString(resource.updatedAt);
    await this.#db.insert({
      tableName: TABLE_RESOURCES,
      record: {
        ...resource,
        metadata: JSON.stringify(resource.metadata),
        createdAt,
        updatedAt,
      },
    });

    return resource;
  }

  async updateResource({
    resourceId,
    workingMemory,
    metadata,
  }: {
    resourceId: string;
    workingMemory?: string;
    metadata?: Record<string, unknown>;
  }): Promise<StorageResourceType> {
    const existingResource = await this.#getResourceById(this.#db.client, resourceId);

    if (!existingResource) {
      const newResource: StorageResourceType = {
        id: resourceId,
        workingMemory,
        metadata: metadata || {},
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      return this.saveResource({ resource: newResource });
    }

    const updatedResource = {
      ...existingResource,
      workingMemory: workingMemory !== undefined ? workingMemory : existingResource.workingMemory,
      metadata: {
        ...existingResource.metadata,
        ...metadata,
      },
      updatedAt: new Date(),
    };

    const tableName = getTableName({ indexName: TABLE_RESOURCES, schemaName: getSchemaName(this.#schema) });

    const updates: string[] = [];
    const values: any[] = [];
    let paramIndex = 1;

    if (workingMemory !== undefined) {
      updates.push(`"workingMemory" = $${paramIndex}`);
      values.push(workingMemory);
      paramIndex++;
    }

    if (metadata) {
      updates.push(`metadata = $${paramIndex}`);
      values.push(JSON.stringify(updatedResource.metadata));
      paramIndex++;
    }

    const updatedAtStr = updatedResource.updatedAt.toISOString();
    updates.push(`"updatedAt" = $${paramIndex++}`);
    values.push(updatedAtStr);
    updates.push(`"updatedAtZ" = $${paramIndex++}`);
    values.push(updatedAtStr);

    values.push(resourceId);

    await this.#db.client.none(`UPDATE ${tableName} SET ${updates.join(', ')} WHERE id = $${paramIndex}`, values);

    return updatedResource;
  }

  async copyThread(args: StorageCloneThreadInput): Promise<StorageCopyThreadOutput> {
    const { sourceThreadId, newThreadId: providedThreadId, resourceId, title, metadata, options } = args;

    // Get the source thread
    const sourceThread = await this.#getThreadById(this.#db.client, { threadId: sourceThreadId });
    if (!sourceThread) {
      throw new MastraError({
        id: createStorageErrorId('PG', 'CLONE_THREAD', 'SOURCE_NOT_FOUND'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.USER,
        text: `Source thread with id ${sourceThreadId} not found`,
        details: { sourceThreadId },
      });
    }

    // Use provided ID or generate a new one
    const newThreadId = providedThreadId || crypto.randomUUID();

    // Check if the new thread ID already exists
    const existingThread = await this.#getThreadById(this.#db.client, { threadId: newThreadId });
    if (existingThread) {
      throw new MastraError({
        id: createStorageErrorId('PG', 'CLONE_THREAD', 'THREAD_EXISTS'),
        domain: ErrorDomain.STORAGE,
        category: ErrorCategory.USER,
        text: `Thread with id ${newThreadId} already exists`,
        details: { newThreadId },
      });
    }

    const threadTableName = getTableName({ indexName: TABLE_THREADS, schemaName: getSchemaName(this.#schema) });
    const messageTableName = getTableName({ indexName: TABLE_MESSAGES, schemaName: getSchemaName(this.#schema) });

    try {
      return await this.#db.client.tx(async t => {
        // Build message query with filters. Only ids (and createdAt for ordering / clone
        // metadata) are read here — content is copied inside the database via
        // INSERT … SELECT and never returned to the JS heap.
        let messageQuery = `SELECT id, "createdAt"
                            FROM ${messageTableName} WHERE thread_id = $1`;
        const messageParams: any[] = [sourceThreadId];
        let paramIndex = 2;

        // Apply date filters
        if (options?.messageFilter?.startDate) {
          messageQuery += ` AND COALESCE("createdAtZ", "createdAt") >= $${paramIndex++}`;
          messageParams.push(options.messageFilter.startDate);
        }
        if (options?.messageFilter?.endDate) {
          messageQuery += ` AND COALESCE("createdAtZ", "createdAt") <= $${paramIndex++}`;
          messageParams.push(options.messageFilter.endDate);
        }

        // Apply message ID filter
        if (options?.messageFilter?.messageIds && options.messageFilter.messageIds.length > 0) {
          messageQuery += ` AND id IN (${options.messageFilter.messageIds.map(() => `$${paramIndex++}`).join(', ')})`;
          messageParams.push(...options.messageFilter.messageIds);
        }

        messageQuery += ` ORDER BY "createdAt" ASC`;

        // Apply message limit (from most recent, so we need to reverse order for limit then sort back)
        if (options?.messageLimit && options.messageLimit > 0) {
          // Get messages ordered DESC to get most recent, limited, then we'll reverse
          const limitQuery = `SELECT * FROM (${messageQuery.replace('ORDER BY "createdAt" ASC', 'ORDER BY "createdAt" DESC')} LIMIT $${paramIndex}) AS limited ORDER BY "createdAt" ASC`;
          messageParams.push(options.messageLimit);
          messageQuery = limitQuery;
        }

        const sourceMessages = await t.manyOrNone<Pick<MessageRowFromDB, 'id' | 'createdAt'>>(
          messageQuery,
          messageParams,
        );

        const now = new Date();
        const nowStr = toUtcISOString(now);

        // Determine the last message ID for clone metadata
        const lastMessageId = sourceMessages.length > 0 ? sourceMessages[sourceMessages.length - 1]!.id : undefined;

        // Create clone metadata
        const cloneMetadata: ThreadCloneMetadata = {
          sourceThreadId,
          clonedAt: now,
          ...(lastMessageId && { lastMessageId }),
        };

        // Create the new thread
        const newThread: StorageThreadType = {
          id: newThreadId,
          resourceId: resourceId || sourceThread.resourceId,
          title: title || (sourceThread.title ? `Clone of ${sourceThread.title}` : ''),
          metadata: {
            ...metadata,
            clone: cloneMetadata,
          },
          createdAt: now,
          updatedAt: now,
        };

        // Insert the new thread
        await t.none(
          `INSERT INTO ${threadTableName} (
            id,
            "resourceId",
            title,
            metadata,
            "createdAt",
            "createdAtZ",
            "updatedAt",
            "updatedAtZ"
          ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8)`,
          [
            newThread.id,
            newThread.resourceId,
            newThread.title,
            newThread.metadata ? JSON.stringify(newThread.metadata) : null,
            nowStr,
            nowStr,
            nowStr,
            nowStr,
          ],
        );

        // Copy messages under new IDs. content/role/type are read from the source row
        // within SQL and never materialized in the JS heap.
        const messageIdMap: Record<string, string> = {};
        const targetResourceId = resourceId || sourceThread.resourceId;

        for (const sourceMsg of sourceMessages) {
          const newMessageId = crypto.randomUUID();
          messageIdMap[sourceMsg.id] = newMessageId;

          const insertResult = await t.query(
            `INSERT INTO ${messageTableName} (id, thread_id, content, "createdAt", "createdAtZ", role, type, "resourceId")
             SELECT $1, $2, content, "createdAt", "createdAtZ", role, type, $3
             FROM ${messageTableName} WHERE id = $4`,
            [newMessageId, newThreadId, targetResourceId, sourceMsg.id],
          );
          if (insertResult.rowCount !== 1) {
            throw new Error(
              `Failed to copy message ${sourceMsg.id}: expected 1 row copied but got ${insertResult.rowCount}`,
            );
          }
        }

        return { thread: newThread, messageIdMap };
      });
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'CLONE_THREAD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { sourceThreadId, newThreadId },
        },
        error,
      );
    }
  }

  // ============================================
  // Observational Memory Methods
  // ============================================

  private getOMKey(threadId: string | null, resourceId: string): string {
    return threadId ? `thread:${threadId}` : `resource:${resourceId}`;
  }

  private parseOMRow(row: any): ObservationalMemoryRecord {
    // OM is a new table - use timezone-aware columns (*Z) directly (no legacy fallback needed)
    return {
      id: row.id,
      scope: row.scope,
      threadId: row.threadId || null,
      resourceId: row.resourceId,
      createdAt: new Date(row.createdAtZ),
      updatedAt: new Date(row.updatedAtZ),
      lastObservedAt: row.lastObservedAtZ ? new Date(row.lastObservedAtZ) : undefined,
      originType: row.originType || 'initial',
      generationCount: Number(row.generationCount || 0),
      activeObservations: row.activeObservations || '',
      // Handle new chunk-based structure
      bufferedObservationChunks: row.bufferedObservationChunks
        ? typeof row.bufferedObservationChunks === 'string'
          ? JSON.parse(row.bufferedObservationChunks)
          : row.bufferedObservationChunks
        : undefined,
      // Deprecated fields (for backward compatibility)
      bufferedObservations: row.activeObservationsPendingUpdate || undefined,
      bufferedObservationTokens: row.bufferedObservationTokens ? Number(row.bufferedObservationTokens) : undefined,
      bufferedMessageIds: undefined, // Use bufferedObservationChunks instead
      bufferedReflection: row.bufferedReflection || undefined,
      bufferedReflectionTokens: row.bufferedReflectionTokens ? Number(row.bufferedReflectionTokens) : undefined,
      bufferedReflectionInputTokens: row.bufferedReflectionInputTokens
        ? Number(row.bufferedReflectionInputTokens)
        : undefined,
      reflectedObservationLineCount: row.reflectedObservationLineCount
        ? Number(row.reflectedObservationLineCount)
        : undefined,
      totalTokensObserved: Number(row.totalTokensObserved || 0),
      observationTokenCount: Number(row.observationTokenCount || 0),
      pendingMessageTokens: Number(row.pendingMessageTokens || 0),
      isReflecting: Boolean(row.isReflecting),
      isObserving: Boolean(row.isObserving),
      isBufferingObservation: row.isBufferingObservation === true || row.isBufferingObservation === 'true',
      isBufferingReflection: row.isBufferingReflection === true || row.isBufferingReflection === 'true',
      lastBufferedAtTokens:
        typeof row.lastBufferedAtTokens === 'number'
          ? row.lastBufferedAtTokens
          : parseInt(String(row.lastBufferedAtTokens ?? '0'), 10) || 0,
      lastBufferedAtTime: row.lastBufferedAtTime ? new Date(String(row.lastBufferedAtTime)) : null,
      config: row.config ? (typeof row.config === 'string' ? JSON.parse(row.config) : row.config) : {},
      metadata: row.metadata ? (typeof row.metadata === 'string' ? JSON.parse(row.metadata) : row.metadata) : undefined,
      observedMessageIds: row.observedMessageIds
        ? typeof row.observedMessageIds === 'string'
          ? JSON.parse(row.observedMessageIds)
          : row.observedMessageIds
        : undefined,
      observedTimezone: row.observedTimezone || undefined,
    };
  }

  async getObservationalMemory(threadId: string | null, resourceId: string): Promise<ObservationalMemoryRecord | null> {
    try {
      const lookupKey = this.getOMKey(threadId, resourceId);
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const result = await this.#db.readClient.oneOrNone(
        `SELECT * FROM ${tableName} WHERE "lookupKey" = $1 ORDER BY "generationCount" DESC LIMIT 1`,
        [lookupKey],
      );
      if (!result) return null;
      return this.parseOMRow(result);
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'GET_OBSERVATIONAL_MEMORY', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { threadId, resourceId },
        },
        error,
      );
    }
  }

  async getObservationalMemoryHistory(
    threadId: string | null,
    resourceId: string,
    limit: number = 10,
    options?: ObservationalMemoryHistoryOptions,
  ): Promise<ObservationalMemoryRecord[]> {
    try {
      const lookupKey = this.getOMKey(threadId, resourceId);
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });

      const conditions = [`"lookupKey" = $1`];
      const params: unknown[] = [lookupKey];
      let paramIndex = 2;

      if (options?.from) {
        conditions.push(`"createdAtZ" >= $${paramIndex}`);
        params.push(options.from.toISOString());
        paramIndex++;
      }
      if (options?.to) {
        conditions.push(`"createdAtZ" <= $${paramIndex}`);
        params.push(options.to.toISOString());
        paramIndex++;
      }

      params.push(limit);
      let sql = `SELECT * FROM ${tableName} WHERE ${conditions.join(' AND ')} ORDER BY "generationCount" DESC LIMIT $${paramIndex}`;
      paramIndex++;

      if (options?.offset != null) {
        params.push(options.offset);
        sql += ` OFFSET $${paramIndex}`;
      }

      const result = await this.#db.readClient.manyOrNone(sql, params);
      if (!result) return [];
      return result.map(row => this.parseOMRow(row));
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'GET_OBSERVATIONAL_MEMORY_HISTORY', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { threadId, resourceId, limit },
        },
        error,
      );
    }
  }

  async initializeObservationalMemory(input: CreateObservationalMemoryInput): Promise<ObservationalMemoryRecord> {
    try {
      const id = crypto.randomUUID();
      const now = new Date();
      const lookupKey = this.getOMKey(input.threadId, input.resourceId);

      const record: ObservationalMemoryRecord = {
        id,
        scope: input.scope,
        threadId: input.threadId,
        resourceId: input.resourceId,
        createdAt: now,
        updatedAt: now,
        lastObservedAt: undefined,
        originType: 'initial',
        generationCount: 0,
        activeObservations: '',
        totalTokensObserved: 0,
        observationTokenCount: 0,
        pendingMessageTokens: 0,
        isReflecting: false,
        isObserving: false,
        isBufferingObservation: false,
        isBufferingReflection: false,
        lastBufferedAtTokens: 0,
        lastBufferedAtTime: null,
        config: input.config,
        observedTimezone: input.observedTimezone,
      };

      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const nowStr = now.toISOString();
      await this.#db.client.none(
        `INSERT INTO ${tableName} (
          id, "lookupKey", scope, "resourceId", "threadId",
          "activeObservations", "activeObservationsPendingUpdate",
          "originType", config, "generationCount", "lastObservedAt", "lastObservedAtZ", "lastReflectionAt", "lastReflectionAtZ",
          "pendingMessageTokens", "totalTokensObserved", "observationTokenCount",
          "isObserving", "isReflecting", "isBufferingObservation", "isBufferingReflection", "lastBufferedAtTokens", "lastBufferedAtTime",
          "observedTimezone", "createdAt", "createdAtZ", "updatedAt", "updatedAtZ"
        ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14, $15, $16, $17, $18, $19, $20, $21, $22, $23, $24, $25, $26, $27, $28)`,
        [
          id,
          lookupKey,
          input.scope,
          input.resourceId,
          input.threadId || null,
          '',
          null,
          'initial',
          JSON.stringify(input.config),
          0,
          null, // lastObservedAt
          null, // lastObservedAtZ
          null, // lastReflectionAt
          null, // lastReflectionAtZ
          0,
          0,
          0,
          false,
          false,
          false, // isBufferingObservation
          false, // isBufferingReflection
          0, // lastBufferedAtTokens
          null, // lastBufferedAtTime
          input.observedTimezone || null,
          nowStr, // createdAt
          nowStr, // createdAtZ
          nowStr, // updatedAt
          nowStr, // updatedAtZ
        ],
      );

      return record;
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'INITIALIZE_OBSERVATIONAL_MEMORY', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { threadId: input.threadId, resourceId: input.resourceId },
        },
        error,
      );
    }
  }

  async insertObservationalMemoryRecord(record: ObservationalMemoryRecord): Promise<void> {
    try {
      const lookupKey = this.getOMKey(record.threadId, record.resourceId);
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const lastObservedAtStr = record.lastObservedAt ? record.lastObservedAt.toISOString() : null;
      const lastBufferedAtTimeStr = record.lastBufferedAtTime ? record.lastBufferedAtTime.toISOString() : null;
      await this.#db.client.none(
        `INSERT INTO ${tableName} (
          id, "lookupKey", scope, "resourceId", "threadId",
          "activeObservations", "activeObservationsPendingUpdate",
          "originType", config, "generationCount", "lastObservedAt", "lastObservedAtZ", "lastReflectionAt", "lastReflectionAtZ",
          "pendingMessageTokens", "totalTokensObserved", "observationTokenCount",
          "observedMessageIds", "bufferedObservationChunks",
          "bufferedReflection", "bufferedReflectionTokens", "bufferedReflectionInputTokens",
          "reflectedObservationLineCount",
          "isObserving", "isReflecting", "isBufferingObservation", "isBufferingReflection",
          "lastBufferedAtTokens", "lastBufferedAtTime",
          "observedTimezone", metadata, "createdAt", "createdAtZ", "updatedAt", "updatedAtZ"
        ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14, $15, $16, $17, $18, $19, $20, $21, $22, $23, $24, $25, $26, $27, $28, $29, $30, $31, $32, $33, $34, $35)`,
        [
          record.id,
          lookupKey,
          record.scope,
          record.resourceId,
          record.threadId || null,
          record.activeObservations || '',
          null,
          record.originType || 'initial',
          record.config ? JSON.stringify(record.config) : null,
          record.generationCount || 0,
          lastObservedAtStr,
          lastObservedAtStr,
          null, // lastReflectionAt
          null, // lastReflectionAtZ
          record.pendingMessageTokens || 0,
          record.totalTokensObserved || 0,
          record.observationTokenCount || 0,
          record.observedMessageIds ? JSON.stringify(record.observedMessageIds) : null,
          record.bufferedObservationChunks ? JSON.stringify(record.bufferedObservationChunks) : null,
          record.bufferedReflection || null,
          record.bufferedReflectionTokens ?? null,
          record.bufferedReflectionInputTokens ?? null,
          record.reflectedObservationLineCount ?? null,
          record.isObserving || false,
          record.isReflecting || false,
          record.isBufferingObservation || false,
          record.isBufferingReflection || false,
          record.lastBufferedAtTokens || 0,
          lastBufferedAtTimeStr,
          record.observedTimezone || null,
          record.metadata ? JSON.stringify(record.metadata) : null,
          record.createdAt.toISOString(),
          record.createdAt.toISOString(),
          record.updatedAt.toISOString(),
          record.updatedAt.toISOString(),
        ],
      );
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'INSERT_OBSERVATIONAL_MEMORY_RECORD', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: record.id, threadId: record.threadId, resourceId: record.resourceId },
        },
        error,
      );
    }
  }

  async updateActiveObservations(input: UpdateActiveObservationsInput): Promise<void> {
    try {
      const now = new Date();
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });

      const lastObservedAtStr = input.lastObservedAt.toISOString();
      const nowStr = now.toISOString();
      const observedMessageIdsJson = input.observedMessageIds ? JSON.stringify(input.observedMessageIds) : null;
      const result = await this.#db.client.query(
        `UPDATE ${tableName} SET
          "activeObservations" = $1,
          "lastObservedAt" = $2,
          "lastObservedAtZ" = $3,
          "pendingMessageTokens" = 0,
          "observationTokenCount" = $4,
          "totalTokensObserved" = "totalTokensObserved" + $5,
          "observedMessageIds" = $6,
          "updatedAt" = $7,
          "updatedAtZ" = $8
        WHERE id = $9`,
        [
          input.observations,
          lastObservedAtStr,
          lastObservedAtStr,
          Math.round(input.tokenCount),
          Math.round(input.tokenCount),
          observedMessageIdsJson,
          nowStr,
          nowStr,
          input.id,
        ],
      );

      if (result.rowCount === 0) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'UPDATE_ACTIVE_OBSERVATIONS', 'NOT_FOUND'),
          text: `Observational memory record not found: ${input.id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.id },
        });
      }
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'UPDATE_ACTIVE_OBSERVATIONS', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.id },
        },
        error,
      );
    }
  }

  async createReflectionGeneration(input: CreateReflectionGenerationInput): Promise<ObservationalMemoryRecord> {
    try {
      const id = crypto.randomUUID();
      const now = new Date();
      const lookupKey = this.getOMKey(input.currentRecord.threadId, input.currentRecord.resourceId);

      const record: ObservationalMemoryRecord = {
        id,
        scope: input.currentRecord.scope,
        threadId: input.currentRecord.threadId,
        resourceId: input.currentRecord.resourceId,
        createdAt: now,
        updatedAt: now,
        lastObservedAt: input.currentRecord.lastObservedAt,
        originType: 'reflection',
        generationCount: input.currentRecord.generationCount + 1,
        activeObservations: input.reflection,
        totalTokensObserved: input.currentRecord.totalTokensObserved,
        observationTokenCount: input.tokenCount,
        pendingMessageTokens: 0,
        isReflecting: false,
        isObserving: false,
        isBufferingObservation: false,
        isBufferingReflection: false,
        lastBufferedAtTokens: 0,
        lastBufferedAtTime: null,
        config: input.currentRecord.config,
        metadata: input.currentRecord.metadata,
        observedTimezone: input.currentRecord.observedTimezone,
      };

      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const nowStr = now.toISOString();
      const lastObservedAtStr = record.lastObservedAt?.toISOString() || null;
      await this.#db.client.none(
        `INSERT INTO ${tableName} (
          id, "lookupKey", scope, "resourceId", "threadId",
          "activeObservations", "activeObservationsPendingUpdate",
          "originType", config, "generationCount", "lastObservedAt", "lastObservedAtZ", "lastReflectionAt", "lastReflectionAtZ",
          "pendingMessageTokens", "totalTokensObserved", "observationTokenCount",
          "isObserving", "isReflecting", "isBufferingObservation", "isBufferingReflection", "lastBufferedAtTokens", "lastBufferedAtTime",
          "observedTimezone", metadata, "createdAt", "createdAtZ", "updatedAt", "updatedAtZ"
        ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10, $11, $12, $13, $14, $15, $16, $17, $18, $19, $20, $21, $22, $23, $24, $25, $26, $27, $28, $29)`,
        [
          id,
          lookupKey,
          record.scope,
          record.resourceId,
          record.threadId || null,
          input.reflection,
          null,
          'reflection',
          JSON.stringify(record.config),
          input.currentRecord.generationCount + 1,
          lastObservedAtStr, // lastObservedAt
          lastObservedAtStr, // lastObservedAtZ
          nowStr, // lastReflectionAt
          nowStr, // lastReflectionAtZ
          record.pendingMessageTokens,
          Math.round(record.totalTokensObserved),
          Math.round(record.observationTokenCount),
          false, // isObserving
          false, // isReflecting
          false, // isBufferingObservation
          false, // isBufferingReflection
          0, // lastBufferedAtTokens
          null, // lastBufferedAtTime
          record.observedTimezone || null,
          record.metadata ? JSON.stringify(record.metadata) : null,
          nowStr, // createdAt
          nowStr, // createdAtZ
          nowStr, // updatedAt
          nowStr, // updatedAtZ
        ],
      );

      return record;
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'CREATE_REFLECTION_GENERATION', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { currentRecordId: input.currentRecord.id },
        },
        error,
      );
    }
  }

  async setReflectingFlag(id: string, isReflecting: boolean): Promise<void> {
    try {
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const nowStr = new Date().toISOString();
      const result = await this.#db.client.query(
        `UPDATE ${tableName} SET "isReflecting" = $1, "updatedAt" = $2, "updatedAtZ" = $3 WHERE id = $4`,
        [isReflecting, nowStr, nowStr, id],
      );

      if (result.rowCount === 0) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'SET_REFLECTING_FLAG', 'NOT_FOUND'),
          text: `Observational memory record not found: ${id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id, isReflecting },
        });
      }
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'SET_REFLECTING_FLAG', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id, isReflecting },
        },
        error,
      );
    }
  }

  async setObservingFlag(id: string, isObserving: boolean): Promise<void> {
    try {
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const nowStr = new Date().toISOString();
      const result = await this.#db.client.query(
        `UPDATE ${tableName} SET "isObserving" = $1, "updatedAt" = $2, "updatedAtZ" = $3 WHERE id = $4`,
        [isObserving, nowStr, nowStr, id],
      );

      if (result.rowCount === 0) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'SET_OBSERVING_FLAG', 'NOT_FOUND'),
          text: `Observational memory record not found: ${id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id, isObserving },
        });
      }
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'SET_OBSERVING_FLAG', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id, isObserving },
        },
        error,
      );
    }
  }

  async setBufferingObservationFlag(id: string, isBuffering: boolean, lastBufferedAtTokens?: number): Promise<void> {
    try {
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const nowStr = new Date().toISOString();

      let query: string;
      let values: any[];

      if (lastBufferedAtTokens !== undefined) {
        query = `UPDATE ${tableName} SET "isBufferingObservation" = $1, "lastBufferedAtTokens" = $2, "updatedAt" = $3, "updatedAtZ" = $4 WHERE id = $5`;
        values = [isBuffering, Math.round(lastBufferedAtTokens), nowStr, nowStr, id];
      } else {
        query = `UPDATE ${tableName} SET "isBufferingObservation" = $1, "updatedAt" = $2, "updatedAtZ" = $3 WHERE id = $4`;
        values = [isBuffering, nowStr, nowStr, id];
      }

      const result = await this.#db.client.query(query, values);

      if (result.rowCount === 0) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'SET_BUFFERING_OBSERVATION_FLAG', 'NOT_FOUND'),
          text: `Observational memory record not found: ${id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id, isBuffering, lastBufferedAtTokens: lastBufferedAtTokens ?? null },
        });
      }
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'SET_BUFFERING_OBSERVATION_FLAG', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id, isBuffering, lastBufferedAtTokens: lastBufferedAtTokens ?? null },
        },
        error,
      );
    }
  }

  async setBufferingReflectionFlag(id: string, isBuffering: boolean): Promise<void> {
    try {
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const nowStr = new Date().toISOString();
      const result = await this.#db.client.query(
        `UPDATE ${tableName} SET "isBufferingReflection" = $1, "updatedAt" = $2, "updatedAtZ" = $3 WHERE id = $4`,
        [isBuffering, nowStr, nowStr, id],
      );

      if (result.rowCount === 0) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'SET_BUFFERING_REFLECTION_FLAG', 'NOT_FOUND'),
          text: `Observational memory record not found: ${id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id, isBuffering },
        });
      }
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'SET_BUFFERING_REFLECTION_FLAG', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id, isBuffering },
        },
        error,
      );
    }
  }

  async clearObservationalMemory(threadId: string | null, resourceId: string): Promise<void> {
    try {
      const lookupKey = this.getOMKey(threadId, resourceId);
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      await this.#db.client.none(`DELETE FROM ${tableName} WHERE "lookupKey" = $1`, [lookupKey]);
    } catch (error) {
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'CLEAR_OBSERVATIONAL_MEMORY', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { threadId, resourceId },
        },
        error,
      );
    }
  }

  async setPendingMessageTokens(id: string, tokenCount: number): Promise<void> {
    try {
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const nowStr = new Date().toISOString();
      const result = await this.#db.client.query(
        `UPDATE ${tableName} SET 
          "pendingMessageTokens" = $1, 
          "updatedAt" = $2,
          "updatedAtZ" = $3
        WHERE id = $4`,
        [Math.round(tokenCount), nowStr, nowStr, id],
      );

      if (result.rowCount === 0) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'SET_PENDING_MESSAGE_TOKENS', 'NOT_FOUND'),
          text: `Observational memory record not found: ${id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id, tokenCount },
        });
      }
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'SET_PENDING_MESSAGE_TOKENS', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id, tokenCount },
        },
        error,
      );
    }
  }

  async updateObservationalMemoryConfig(input: UpdateObservationalMemoryConfigInput): Promise<void> {
    try {
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });

      // Read current config
      const selectResult = await this.#db.client.query(`SELECT config FROM ${tableName} WHERE id = $1`, [input.id]);

      if (selectResult.rowCount === 0) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'UPDATE_OM_CONFIG', 'NOT_FOUND'),
          text: `Observational memory record not found: ${input.id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.id },
        });
      }

      const row = selectResult.rows[0];
      const existing: Record<string, unknown> = row.config
        ? typeof row.config === 'string'
          ? JSON.parse(row.config)
          : row.config
        : {};
      const merged = this.deepMergeConfig(existing, input.config);
      const nowStr = new Date().toISOString();

      await this.#db.client.query(
        `UPDATE ${tableName} SET config = $1, "updatedAt" = $2, "updatedAtZ" = $3 WHERE id = $4`,
        [JSON.stringify(merged), nowStr, nowStr, input.id],
      );
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'UPDATE_OM_CONFIG', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.id },
        },
        error,
      );
    }
  }

  // ============================================
  // Async Buffering Methods
  // ============================================

  async updateBufferedObservations(input: UpdateBufferedObservationsInput): Promise<void> {
    try {
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const nowStr = new Date().toISOString();

      // Create new chunk with ID and timestamp
      const newChunk: BufferedObservationChunk = {
        id: `ombuf-${randomUUID()}`,
        cycleId: input.chunk.cycleId,
        observations: input.chunk.observations,
        tokenCount: Math.round(input.chunk.tokenCount),
        messageIds: input.chunk.messageIds,
        messageTokens: Math.round(input.chunk.messageTokens ?? 0),
        lastObservedAt: input.chunk.lastObservedAt,
        createdAt: new Date(),
        suggestedContinuation: input.chunk.suggestedContinuation,
        currentTask: input.chunk.currentTask,
        threadTitle: input.chunk.threadTitle,
        extractedValues: input.chunk.extractedValues,
        extractionFailures: input.chunk.extractionFailures,
      };

      // Append the chunk only if this cycle has not already been persisted by a previous attempt.
      const lastBufferedAtTime = input.lastBufferedAtTime ? input.lastBufferedAtTime.toISOString() : null;
      const result = await this.#db.client.query(
        `UPDATE ${tableName} SET
          "bufferedObservationChunks" = CASE
            WHEN EXISTS (
              SELECT 1
              FROM jsonb_array_elements(COALESCE("bufferedObservationChunks", '[]'::jsonb)) AS chunk
              WHERE chunk->>'cycleId' = $2
            ) THEN COALESCE("bufferedObservationChunks", '[]'::jsonb)
            ELSE COALESCE("bufferedObservationChunks", '[]'::jsonb) || $1::jsonb
          END,
          "lastBufferedAtTime" = COALESCE($3, "lastBufferedAtTime"),
          "updatedAt" = $4,
          "updatedAtZ" = $5
        WHERE id = $6`,
        [JSON.stringify([newChunk]), input.chunk.cycleId, lastBufferedAtTime, nowStr, nowStr, input.id],
      );

      if (result.rowCount === 0) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'UPDATE_BUFFERED_OBSERVATIONS', 'NOT_FOUND'),
          text: `Observational memory record not found: ${input.id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.id },
        });
      }
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'UPDATE_BUFFERED_OBSERVATIONS', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.id },
        },
        error,
      );
    }
  }

  async swapBufferedToActive(input: SwapBufferedToActiveInput): Promise<SwapBufferedToActiveResult> {
    try {
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const nowStr = new Date().toISOString();

      // Get current record
      const record = await this.#db.client.oneOrNone(`SELECT * FROM ${tableName} WHERE id = $1`, [input.id]);
      if (!record) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'SWAP_BUFFERED_TO_ACTIVE', 'NOT_FOUND'),
          text: `Observational memory record not found: ${input.id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.id },
        });
      }

      // Parse buffered chunks
      let chunks: BufferedObservationChunk[] = [];
      if (record.bufferedObservationChunks) {
        try {
          const parsed =
            typeof record.bufferedObservationChunks === 'string'
              ? JSON.parse(record.bufferedObservationChunks)
              : record.bufferedObservationChunks;
          chunks = Array.isArray(parsed) ? parsed : [];
        } catch {
          chunks = [];
        }
      }

      if (chunks.length === 0) {
        return {
          chunksActivated: 0,
          messageTokensActivated: 0,
          observationTokensActivated: 0,
          messagesActivated: 0,
          activatedCycleIds: [],
          activatedMessageIds: [],
        };
      }

      // Calculate target message tokens to activate based on new formula:
      // retentionFloor = threshold * (1 - ratio) represents tokens to keep as raw messages
      // targetMessageTokens = max(0, currentPending - retentionFloor) represents tokens to activate
      const retentionFloor = input.messageTokensThreshold * (1 - input.activationRatio);
      const targetMessageTokens = Math.max(0, input.currentPendingTokens - retentionFloor);

      // Find the closest chunk boundary to the target, biased over (prefer removing
      // slightly more than the target so remaining context lands at or below retentionFloor).
      // Track both best-over and best-under boundaries so we can fall back to under
      // if the over boundary would overshoot by too much.
      let cumulativeMessageTokens = 0;
      let chunksToActivate = 0;
      let bestOverBoundary = 0;
      let bestOverTokens = 0;
      let bestUnderBoundary = 0;
      let bestUnderTokens = 0;

      for (let i = 0; i < chunks.length; i++) {
        cumulativeMessageTokens += chunks[i]!.messageTokens ?? 0;
        const boundary = i + 1;

        if (cumulativeMessageTokens >= targetMessageTokens) {
          // Over or equal — track the closest (lowest) over boundary
          if (bestOverBoundary === 0 || cumulativeMessageTokens < bestOverTokens) {
            bestOverBoundary = boundary;
            bestOverTokens = cumulativeMessageTokens;
          }
        } else {
          // Under — track the closest (highest) under boundary
          if (cumulativeMessageTokens > bestUnderTokens) {
            bestUnderBoundary = boundary;
            bestUnderTokens = cumulativeMessageTokens;
          }
        }
      }

      // Safeguard: if the over boundary would eat into more than 95% of the
      // retention floor, fall back to the best under boundary instead.
      // This prevents edge cases where a large chunk overshoots dramatically.
      // When forceMaxActivation is set (above blockAfter), still prefer the over
      // boundary, but never if it would leave fewer than the smaller of 1000
      // tokens or the retention floor remaining.
      const maxOvershoot = retentionFloor * 0.95;
      const overshoot = bestOverTokens - targetMessageTokens;
      const remainingAfterOver = input.currentPendingTokens - bestOverTokens;
      const remainingAfterUnder = input.currentPendingTokens - bestUnderTokens;
      // When activationRatio ≈ 1.0, retentionFloor is 0 and minRemaining becomes 0 — intentional for "activate everything" configs.
      const minRemaining = Math.min(1000, retentionFloor);

      if (input.forceMaxActivation && bestOverBoundary > 0 && remainingAfterOver >= minRemaining) {
        chunksToActivate = bestOverBoundary;
      } else if (bestOverBoundary > 0 && overshoot <= maxOvershoot && remainingAfterOver >= minRemaining) {
        chunksToActivate = bestOverBoundary;
      } else if (bestUnderBoundary > 0 && remainingAfterUnder >= minRemaining) {
        chunksToActivate = bestUnderBoundary;
      } else if (bestOverBoundary > 0) {
        // All boundaries are over and exceed the safeguard — still activate
        // the closest over boundary (better than nothing)
        chunksToActivate = bestOverBoundary;
      } else {
        chunksToActivate = 1;
      }

      // Split chunks
      const activatedChunks = chunks.slice(0, chunksToActivate);
      const remainingChunks = chunks.slice(chunksToActivate);

      // Combine activated observations
      const activatedContent = activatedChunks.map(c => c.observations).join('\n\n');
      const activatedTokens = Math.round(activatedChunks.reduce((sum, c) => sum + c.tokenCount, 0));
      const activatedMessageTokens = Math.round(activatedChunks.reduce((sum, c) => sum + (c.messageTokens ?? 0), 0));
      const activatedMessageCount = activatedChunks.reduce((sum, c) => sum + c.messageIds.length, 0);
      const activatedCycleIds = activatedChunks.map(c => c.cycleId).filter((id): id is string => !!id);
      const activatedMessageIds = activatedChunks.flatMap(c => c.messageIds ?? []);

      // Derive lastObservedAt from the latest activated chunk, or use provided value
      const latestChunk = activatedChunks[activatedChunks.length - 1];
      const lastObservedAt =
        input.lastObservedAt ?? (latestChunk?.lastObservedAt ? new Date(latestChunk.lastObservedAt) : new Date());
      const lastObservedAtStr = lastObservedAt.toISOString();

      // NOTE: We intentionally do NOT add message IDs to observedMessageIds during buffered activation.
      // Buffered chunks represent observations of messages as they were at buffering time.
      // With streaming, messages grow after buffering, so we rely on lastObservedAt for filtering.
      // New content after lastObservedAt will be picked up in subsequent observations.

      // Atomic conditional update — the WHERE clause ensures chunks haven't already
      // been swapped by a concurrent run. If another run cleared the chunks first,
      // this UPDATE matches 0 rows and we return early with chunksActivated: 0.
      // Include message boundary delimiter for cache stability.
      const boundary = `\n\n--- message boundary (${lastObservedAt.toISOString()}) ---\n\n`;
      const updateResult = await this.#db.client.query(
        `UPDATE ${tableName} SET
          "activeObservations" = CASE
            WHEN "activeObservations" IS NOT NULL AND "activeObservations" != ''
            THEN "activeObservations" || $10 || $1
            ELSE $1
          END,
          "observationTokenCount" = COALESCE("observationTokenCount", 0) + $2,
          "pendingMessageTokens" = GREATEST(0, COALESCE("pendingMessageTokens", 0) - $3),
          "bufferedObservationChunks" = $4,
          "lastObservedAt" = $5,
          "lastObservedAtZ" = $6,
          "updatedAt" = $7,
          "updatedAtZ" = $8
        WHERE id = $9
          AND "bufferedObservationChunks" IS NOT NULL
          AND "bufferedObservationChunks"::text != '[]'`,
        [
          activatedContent,
          activatedTokens,
          activatedMessageTokens,
          remainingChunks.length > 0 ? JSON.stringify(remainingChunks) : null,
          lastObservedAtStr,
          lastObservedAtStr,
          nowStr,
          nowStr,
          input.id,
          boundary,
        ],
      );

      if (updateResult.rowCount === 0) {
        return {
          chunksActivated: 0,
          messageTokensActivated: 0,
          observationTokensActivated: 0,
          messagesActivated: 0,
          activatedCycleIds: [],
          activatedMessageIds: [],
        };
      }

      // Use hints from the most recent activated chunk only — stale hints from older chunks are discarded
      const latestChunkHints = activatedChunks[activatedChunks.length - 1];

      return {
        chunksActivated: activatedChunks.length,
        messageTokensActivated: activatedMessageTokens,
        observationTokensActivated: activatedTokens,
        messagesActivated: activatedMessageCount,
        activatedCycleIds,
        activatedMessageIds,
        observations: activatedContent,
        perChunk: activatedChunks.map(c => ({
          cycleId: c.cycleId ?? '',
          messageTokens: c.messageTokens ?? 0,
          observationTokens: c.tokenCount,
          messageCount: c.messageIds.length,
          observations: c.observations,
        })),
        suggestedContinuation: latestChunkHints?.suggestedContinuation ?? undefined,
        currentTask: latestChunkHints?.currentTask ?? undefined,
      };
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'SWAP_BUFFERED_TO_ACTIVE', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.id },
        },
        error,
      );
    }
  }

  async updateBufferedReflection(input: UpdateBufferedReflectionInput): Promise<void> {
    try {
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });
      const nowStr = new Date().toISOString();

      // Append reflection to existing buffered content
      const result = await this.#db.client.query(
        `UPDATE ${tableName} SET
          "bufferedReflection" = CASE 
            WHEN "bufferedReflection" IS NOT NULL AND "bufferedReflection" != '' 
            THEN "bufferedReflection" || E'\\n\\n' || $1
            ELSE $1
          END,
          "bufferedReflectionTokens" = COALESCE("bufferedReflectionTokens", 0) + $2,
          "bufferedReflectionInputTokens" = COALESCE("bufferedReflectionInputTokens", 0) + $3,
          "reflectedObservationLineCount" = $4,
          "updatedAt" = $5,
          "updatedAtZ" = $6
        WHERE id = $7`,
        [
          input.reflection,
          Math.round(input.tokenCount),
          Math.round(input.inputTokenCount),
          input.reflectedObservationLineCount,
          nowStr,
          nowStr,
          input.id,
        ],
      );

      if (result.rowCount === 0) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'UPDATE_BUFFERED_REFLECTION', 'NOT_FOUND'),
          text: `Observational memory record not found: ${input.id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.id },
        });
      }
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'UPDATE_BUFFERED_REFLECTION', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.id },
        },
        error,
      );
    }
  }

  async swapBufferedReflectionToActive(input: SwapBufferedReflectionToActiveInput): Promise<ObservationalMemoryRecord> {
    try {
      const tableName = getTableName({
        indexName: OM_TABLE,
        schemaName: getSchemaName(this.#schema),
      });

      // Get current record to calculate split
      const record = await this.#db.client.oneOrNone(`SELECT * FROM ${tableName} WHERE id = $1`, [
        input.currentRecord.id,
      ]);
      if (!record) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'SWAP_BUFFERED_REFLECTION_TO_ACTIVE', 'NOT_FOUND'),
          text: `Observational memory record not found: ${input.currentRecord.id}`,
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.currentRecord.id },
        });
      }

      const bufferedReflection = record.bufferedReflection || '';
      const reflectedLineCount = Number(record.reflectedObservationLineCount || 0);

      if (!bufferedReflection) {
        throw new MastraError({
          id: createStorageErrorId('PG', 'SWAP_BUFFERED_REFLECTION_TO_ACTIVE', 'NO_CONTENT'),
          text: 'No buffered reflection to swap',
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.USER,
          details: { id: input.currentRecord.id },
        });
      }

      // Split current activeObservations by the recorded boundary.
      // Lines 0..reflectedLineCount were reflected on → replaced by bufferedReflection.
      // Lines after reflectedLineCount were added after reflection started → kept as-is.
      const currentObservations = (record.activeObservations as string) || '';
      const allLines = currentObservations.split('\n');
      const unreflectedLines = allLines.slice(reflectedLineCount);
      const unreflectedContent = unreflectedLines.join('\n').trim();

      // New activeObservations = bufferedReflection + unreflected observations
      const newObservations = unreflectedContent
        ? `${bufferedReflection}\n\n${unreflectedContent}`
        : bufferedReflection;

      // Create new generation with the merged content.
      // tokenCount is computed by the processor using its token counter on the combined content.
      const newRecord = await this.createReflectionGeneration({
        currentRecord: input.currentRecord,
        reflection: newObservations,
        tokenCount: input.tokenCount,
      });

      // Clear buffered state on old record
      const nowStr = new Date().toISOString();
      await this.#db.client.query(
        `UPDATE ${tableName} SET
          "bufferedReflection" = NULL,
          "bufferedReflectionTokens" = NULL,
          "bufferedReflectionInputTokens" = NULL,
          "reflectedObservationLineCount" = NULL,
          "updatedAt" = $1,
          "updatedAtZ" = $2
        WHERE id = $3`,
        [nowStr, nowStr, input.currentRecord.id],
      );

      return newRecord;
    } catch (error) {
      if (error instanceof MastraError) {
        throw error;
      }
      throw new MastraError(
        {
          id: createStorageErrorId('PG', 'SWAP_BUFFERED_REFLECTION_TO_ACTIVE', 'FAILED'),
          domain: ErrorDomain.STORAGE,
          category: ErrorCategory.THIRD_PARTY,
          details: { id: input.currentRecord.id },
        },
        error,
      );
    }
  }
}
